/**
 * Capability renderer registry.
 *
 * Core rendering knows only that a capability id names a registered renderer.
 * The force graph is the first real entry: its role contract arrives through IR
 * channels, while its data and coordinates remain host-owned view state.
 */
import {
  ROLE_KIND, registerModule, type ModuleDescriptor, type RegionPurpose, type Role,
} from '@morph/spec';
import type { IR, IRNode } from './ir.js';
import { MIN_TARGET_PX } from './a11y.js';
import { anchorAttrs, type ObjectHandle } from './edit.js';
import { FORCE_GRAPH_EDGE_LIMIT, FORCE_GRAPH_LIMIT, type GraphPoint } from './view-state.js';
import { toneOf } from './tone.js';

export interface ForceGraphInput {
  readonly nodes: readonly { readonly handle: ObjectHandle; readonly label: string }[];
  readonly edges: readonly { readonly from: ObjectHandle; readonly to: ObjectHandle }[];
  readonly positions: ReadonlyMap<ObjectHandle, GraphPoint>;
  /**
   * The render pass, WITHHELD WITHOUT A WRITE GRANT — task `0182`, decision
   * `0062`. Required until now, so a module could not express "no write
   * grant" the way the core renderer could, and a reader driving the vault
   * held ten epochs on the graph's own nodes while holding zero on a lens
   * that draws no module. The handle still goes out unconditionally: identity
   * is not permission, and a reader must still be able to follow what they
   * can see.
   */
  readonly epoch?: number;
  readonly truncated?: boolean;
  /** The node the graph is ABOUT — a workspace pane's local graph is
   *  centred on the open note (decision 0055), and the renderer marks it
   *  `aria-current`. Absent for the page graph, which is about the pool. */
  readonly current?: ObjectHandle;
}

/**
 * The box a graph drawn INSIDE A WORKSPACE PANE is laid out for — the spec
 * asks with `config.pane: true` (the template does; 0055), the host lays
 * out into this box and the renderer draws it at this size, so the two
 * cannot disagree about the canvas. The page graph keeps 1000×560.
 */
// 420 tall, not 520: the seventh verdict found the scope summary stranded
// far below a compact cluster; a shorter box keeps the caption beside the
// graph it describes without touching the layout's scale.
export const FORCE_GRAPH_PANE_BOX = { width: 300, height: 420 } as const;
/** The VAULT graph's box — the main column's canvas (`config.vault`; the
 *  tenth verdict's Graph view). A viewBox the stylesheet scales to the
 *  column, so the picture is the topology and not a fixed 1000px page. */
export const FORCE_GRAPH_VAULT_BOX = { width: 1000, height: 640 } as const;

/**
 * One plotted observation, already resolved by the host.
 *
 * `x` and `y` arrive as numbers because turning a role value into a coordinate
 * is the host's job, exactly as turning an object id into a handle is: a date
 * becomes epoch milliseconds, a score becomes its number. The renderer receives
 * geometry and labels and never sees a store value — the same boundary the
 * force graph draws, for the same reason.
 */
export interface PlotPoint {
  readonly handle: ObjectHandle;
  readonly x: number;
  readonly y: number;
  /** Optional grouping, from the `series` channel. */
  readonly series?: string;
  /** What a person hears when the mark takes focus. */
  readonly label: string;
  /** The x and y values as a person would read them. Optional, and the reason
   *  they exist: `x` is a NUMBER — epoch milliseconds for a date axis — so a
   *  name built from it announces "1594166400000" where it means "July 2020".
   *  The host knows the formatting; the renderer knows the layout. When absent
   *  the raw value is announced, which is right for a score and poor for a
   *  date, so a host with a date axis should supply these. */
  readonly xText?: string;
  readonly yText?: string;
}

export interface ContinuousPlotInput {
  readonly points: readonly PlotPoint[];
  /** Axis captions. Host-supplied, because the role's human name is the
   *  manifest's business and the renderer has no access to it. */
  readonly xLabel: string;
  readonly yLabel: string;
  /** True when the host could not fit the whole set. A plot of a sample,
   *  captioned as though it were the whole, is the failure this refuses. */
  readonly truncated?: boolean;
}

/**
 * One entry of a document's note apparatus, derived by the host.
 *
 * A footnote is a THING THE DATA ALREADY SAYS, not a thing somebody typed into
 * a second field: a section that cites another object has a citation, and the
 * apparatus is that citation written where an apparatus goes. So the host
 * derives these from the objects it is about to render, the same way it derives
 * the table of contents, and for the same reason — by the time the module sees
 * the body it is HTML.
 *
 * Both anchors are minted by the host with `sectionAnchor` and `noteAnchor` and
 * checked here with `isSectionAnchor` and `isNoteAnchor`. They arrive as data,
 * and data is where an untrusted value arrives; escaping would keep the markup
 * well-formed and would still let a caller point a note at anything on the page.
 */
export interface DocumentNote {
  /** The section this note annotates — where its back-link goes. */
  readonly anchor: string;
  /** This note's own id — where the section's marker goes. */
  readonly note: string;
  /** What the note says. */
  readonly text: string;
}

/** What a document module needs that it cannot see: the sections its body slot
 *  is about to render, named and anchored by the host — and the notes those
 *  sections carry, for the same reason. */
export interface DocumentFlowInput {
  readonly sections: readonly { readonly anchor: string; readonly name: string }[];
  /** Absent and empty mean the same thing: no apparatus. A document whose
   *  sections cite nothing has no notes, and that is not an error. */
  readonly notes?: readonly DocumentNote[];
}

/**
 * What a calendar needs that it cannot see: which objects fall on which day.
 *
 * The host resolves the dates, because only the host can. A date reaches the
 * renderer as whatever the store holds — a `Date`, an ISO string, something a
 * user typed — and parsing that is a store concern with a store's rules. What
 * crosses this boundary is already a decision: this object, this day.
 *
 * `day` is a plain `YYYY-MM-DD` string rather than a `Date` for the same reason
 * `ObjectHandle` is opaque: a `Date` carries a time zone, and a grid cell that
 * shifts by one day depending on where the reader is sitting is a calendar
 * that lies. The host has already chosen a calendar day; this is that choice,
 * written down.
 */
export interface CalendarInput {
  /** The month the grid is drawn around, as `YYYY-MM`. */
  readonly month: string;
  readonly events: readonly {
    readonly day: string;
    readonly label: string;
    readonly handle?: ObjectHandle;
  }[];
  /** The render epoch, so an event resolves back to its object through the same
   *  anchor path a graph node does. Without it a calendar would be a picture of
   *  a collection you cannot open anything from.
   *
   *  OPTIONAL since `0182`: a reader gets none, and the OPENING still works
   *  because that resolves through the handle. */
  readonly epoch?: number;
  /** Objects the lens is showing that carry no usable date. Reported rather
   *  than dropped: a calendar that silently omits half a collection is the
   *  "accepted and discarded" defect this repo keeps finding. */
  readonly undated?: number;
  /** Objects that ARE dated but fall outside the month on screen.
   *
   *  A separate count from `undated`, and the more important one. A pool of
   *  films spread over sixty years puts one film in the busiest month, so a
   *  calendar drawing that month shows one object out of twelve — and without
   *  this it says nothing at all about the other eleven. "Not here" and "not
   *  dated" are different answers and a reader needs to be able to tell them
   *  apart. */
  readonly elsewhere?: number;
  /** Months with at least one object, ascending, so the module can offer a way
   *  to reach them. A calendar you cannot leave is a dead end when the data is
   *  sparse — which is the normal case, not the edge one. */
  readonly months?: readonly string[];
  /** The host's TODAY, `YYYY-MM-DD` — given only by a host that answers ANY
   *  month, and then the way out is the app's: the adjacent months and a
   *  Today control, the day itself marked on the grid (Notion's calendar).
   *  Absent, the steps go between the months that have objects, as before.
   *  The host's clock, never the module's: a render that reads `Date.now()`
   *  is a render nobody can reproduce. */
  readonly today?: string;
}

/**
 * What a timeline needs that it cannot work out: where each object sits in
 * time, resolved to a single scale by the host.
 *
 * Positions are FRACTIONS of the axis, 0 to 1, not dates. The module draws; it
 * does not decide what "halfway through 2024" means, and handing it two ISO
 * strings would make it parse dates — the exact job the calendar already
 * proved has a time-zone trap in it. The host owns the scale, so the host does
 * the arithmetic once and every bar agrees.
 *
 * A bar with `to === from` is a MOMENT: an object with one date rather than a
 * span. Drawn as a marker, because a zero-width rectangle is invisible and an
 * object that has a date should not vanish for having only one.
 */
export interface TimelineInput {
  /** What the two ends of the axis read as. Text, because the host has already
   *  decided the granularity — a year, a month, a day. */
  readonly from: string;
  readonly to: string;
  readonly bars: readonly {
    /** 0 = the left end of the axis, 1 = the right. */
    readonly from: number;
    readonly to: number;
    readonly label: string;
    readonly lane?: string;
    /** The span has a start and no known end — an unfinished thing. Drawn with
     *  an open right edge, because a bar that stops at "last touched" is
     *  otherwise indistinguishable from one that stopped because the work
     *  finished, and that is a chart telling a reader something untrue. */
    readonly open?: boolean;
    readonly handle?: ObjectHandle;
  }[];
  /** Withheld without a write grant — task `0182`. */
  readonly epoch?: number;
  /** Objects the lens is showing that carry no usable date. Said, never
   *  dropped — the same count the calendar reports, for the same reason. */
  readonly undated?: number;
  /** The lanes in the host's DECLARED order, when it has one — a status
   *  property's options — so a look colours a lane as it tints the board's
   *  column of the same value, whatever order the bars sort into. Lanes
   *  not named here follow, first-seen. */
  readonly lanes?: readonly string[];
}

/**
 * The workspace chrome's host-resolved facts — decision 0055's `app.obsidian`.
 *
 * A tab strip names the OPEN object and a status bar counts what is in it;
 * both are facts the host reads through the projection (the `label` and
 * `words` channels say which roles) and hands over as text and numbers. The
 * renderer never sees a value it was not given: an absent input draws the
 * chrome empty rather than inventing a title.
 */
export interface WorkspaceTabsInput {
  /** Tabs in strip order; exactly one is `active`. An inactive tab carries
   *  the opaque HANDLE of the object it would reopen, so the host can route
   *  its activation — a tab that cannot be pressed is an advertisement
   *  (@Codex, P2 on d0c42da). */
  readonly tabs: readonly {
    readonly label: string; readonly active?: boolean; readonly handle?: ObjectHandle;
  }[];
  /** The strip's `+` is a New note button when the host says a writer is
   *  here; otherwise it is not drawn (a `+` that makes nothing is a lie). */
  readonly create?: boolean;
  /** The MARK on the active tab's close control, declared by the look —
   *  never a renderer constant. Drawn inert (0056) when the host cannot back
   *  it, and as a real button when `close` below says it can. The look owns
   *  the glyph either way; whether it acts is the host's to say. */
  readonly inert?: { readonly close?: InertItem };
  /**
   * The host CLOSES a tab — task `0198`.
   *
   * Absent, the × is imagery: `0056` endorses drawing chrome that nothing
   * backs, and an unbacked × announced as a control would be worse. Present,
   * the same glyph becomes a button carrying the tab's own handle, because a
   * strip that only grows is not a tab strip either. Closing is a VIEW
   * action, so it is not gated on a write grant — a reader may tidy the
   * strip they are reading.
   */
  readonly close?: boolean;
}
/**
 * THE WINDOW'S OWN CONTROLS — a row above the columns, in the shell's leading
 * band, for a look whose reference is a desktop window.
 *
 * A capability rather than a field on the ribbon or the tab strip, because
 * @Codex's ruling on `470dfd5` is that neither of those is the right carrier:
 * the tab strip begins after the ribbon and the explorer, so controls drawn
 * there read as decoration on the active tab; and a 44px ribbon cannot hold a
 * group that spans 50px without overlaying the boundary beside it. What the
 * controls need is a row of their own that the columns begin BENEATH.
 *
 * It carries `InertItem`s and nothing else: 0056 says the app's chrome the host
 * cannot back is drawn as imagery, and a window control is the purest case —
 * it names no object, counts nothing, and does nothing.
 */
export interface WorkspaceWindowInput {
  /** In drawing order. The look says which controls its window has and what
   *  colour each one is; nothing here knows what a traffic light is. */
  readonly controls: readonly InertItem[];
}
/**
 * THE RIBBON'S PANE SELECTORS — @Codex's ruling on `2e06944`, and the whole of
 * what it asks for beyond hiding a row.
 *
 * Task `0194` moved the look's tool strip into the window band, which left the
 * left sidebar drawing its OWN Files/Search tablist underneath: two controls
 * for one selection in two rows, where the reference's band icons ARE the
 * pane tabs and the tree starts immediately beneath. @Codex ruled for keeping
 * the band and suppressing the row — with one condition that is the point:
 *
 *   *"Do not merely hide the row while leaving the band controls semantically
 *   weaker: the band must become the authoritative pane selector."*
 *
 * So the semantics MOVE. `selects` says which of this strip's tools choose a
 * pane and which pane each chooses; `active` says which is chosen now. Those
 * tools then draw as a real `tablist` of `tab`s carrying `aria-selected`, and
 * the tools that ACT rather than select — new, graph, the exit — stay beside
 * the list rather than inside it, which is the rule `workspaceTabs` already
 * follows for its `+`: a command in a tablist gives assistive technology an
 * invalid model of the widget.
 *
 * Absent, the strip draws exactly as it did: a look that puts its ribbon
 * somewhere other than a window band owes nothing here.
 */
/** One tool on the activity ribbon: what it does, what it is called, and the
 *  path it is drawn with. The glyph travels with the declaration for the same
 *  reason a search trigger's does — a look that wants a tool this file has
 *  never heard of supplies all three and needs no edit here. */
export interface RibbonTool {
  readonly action: string;
  readonly label: string;
  /** An SVG path `d`, drawn in a 24x24 box. */
  readonly glyph: string;
}
export interface WorkspaceRibbonInput {
  /** Tool action name to the pane purpose it selects, e.g. `files` to
   *  `explorer`. From the host, because which action means which pane is a
   *  fact about the app being reproduced. */
  readonly selects?: Readonly<Record<string, RegionPurpose>>;
  /** The strip's INVENTORY, in order — @Codex's P2 on `d80adab`.
   *
   *  It was a fixed table inside the renderer, so `selects` accepted an action
   *  the table had no row for and the mapping was then filtered away in
   *  silence: a seventh look wanting a different ribbon tool had to edit this
   *  shared file, which is the thing `0173` measures. Omitted, the four
   *  Obsidian's strip carries are the default, so a look that says nothing
   *  draws what it drew.
   *
   *  A LOOK RECORD REACHES THIS — task `0204`, closed 2026-09-06, and this
   *  comment said the opposite until 2026-09-07. `LookRecord` carries
   *  `ribbonTools` beside `ribbonSelects`, so the demo's vocabulary no longer
   *  stops one step short; no look declares either yet, which is a different
   *  sentence and the one that is true. Corrected because a comment asserting
   *  a property the code lacks is a bug, and this one asserted a LACK the
   *  code had grown out of — the same defect facing the other way. */
  readonly tools?: readonly RibbonTool[];
  /** The pane showing now, resolved by the renderer against what the sidebar
   *  holds — never an id. */
  readonly active?: RegionPurpose;
}
export interface WorkspaceStatusInput {
  readonly words?: number;
  readonly characters?: number;
  readonly backlinks?: number;
  /** What the vault is called, drawn with the local-first dot. */
  readonly vault?: string;
  /** The format the bar names at its end — Obsidian's "Markdown". A bar
   *  for an app that names none (Word) leaves it out. */
  readonly format?: string;
  /** Chrome the app shows and the host cannot back — decision 0056:
   *  drawn as imagery, never as a control. `end` items sit at the bar's
   *  right (Word's view icons and zoom). */
  readonly inert?: readonly InertItem[];
}

/**
 * INERT PRESENTATION — decision 0056. Chrome the reference app shows and
 * the host cannot truthfully back is drawn from a declared item, never a
 * renderer constant: not a button or link, not focusable, no role, no
 * accessible name, `aria-hidden`, no hover or cursor. The pixels are the
 * app's; nothing lies to a keyboard or a screen reader. `glyph` is a text
 * glyph, or an SVG path (`M…`) drawn at the item's size; `big` stacks the
 * glyph over the label (Word's Paste); `box` draws a bordered field (a
 * font name, a search box); `place` is where the bar puts it.
 */
export interface InertItem {
  readonly label: string;
  readonly glyph?: string;
  readonly big?: boolean;
  readonly box?: boolean;
  /** `actions`: among a topbar's page actions, before the star. */
  readonly place?: 'start' | 'centre' | 'end' | 'actions';
  /** A colour swatch bar under the glyph (Word's highlight yellow, font
   *  colour red, shading blue) — a CSS colour. */
  readonly bar?: string;
  /** A small ▾ after the item (a split button's dropdown half). */
  readonly menu?: boolean;
  /** A thin separator before the item (the first of a cluster). */
  readonly sep?: boolean;
  /** A line break before the item: the group's rows are the app's own,
   *  not what the width happens to wrap. */
  readonly break?: boolean;
/** A filled circle of a declared CSS colour, drawn INSTEAD of a glyph: a
   *  window control in a look whose reference is a desktop window.
   *
   *  A colour and not a name, for the same reason `bar` is one — the three
   *  macOS lights are facts about macOS, so the look that reproduces a macOS
   *  window declares them and a look reproducing a different window declares
   *  something else. Nothing in the renderer knows what a traffic light is. */
  readonly dot?: string;
}
/** A RIBBON over a document (Word): one tab, shown active, and a band of
 *  groups with their labels beneath. Each item is a host action, routed by
 *  `data-ws-action`; a tile is a style-gallery preview drawn in its own
 *  style. A group with no items is not drawn — nothing decorative. */
export interface DocRibbonInput {
  readonly tab: string;
  /** The tab row the app shows, the active `tab` among them; the others
   *  are inert (0056) — a tab with nothing behind it is imagery. Absent,
   *  the active tab alone is drawn. */
  readonly tabs?: readonly string[];
  /** Inert items at the tab row's right (Word's Comments, Editing, Share). */
  readonly right?: readonly InertItem[];
  readonly groups: readonly {
    readonly label: string;
    /** A backed item carries an `action`; an inert one (0056) carries
     *  none and says so — an item with neither is refused in words. */
    readonly items: readonly (
      | { readonly label: string; readonly action: string; readonly tile?: boolean; readonly big?: boolean; readonly glyph?: string; readonly inert?: false }
      | (InertItem & { readonly inert: true; readonly tile?: boolean })
    )[];
  }[];
}

/** The Search tool's input: the host's query, echoed so a repaint keeps
 *  what was typed, and how many hits it answered — absent while the query
 *  is empty, so the box says nothing about counts it did not compute. */
export interface WorkspaceSearchInput {
  readonly query: string;
  readonly count?: number;
  /** The box's own words when the host has them — Fibery's "Search this
   *  view…" against the vault's "Search…". Presentation, never data. */
  readonly placeholder?: string;
  /** A heading over the box (Fabric's "AI assistant") and inert chrome
   *  beside it (0056): the model pill in the header, the chip and the
   *  actions inside the composer. */
  readonly heading?: string;
  readonly inert?: readonly InertItem[];
  /** A look whose row has no room for a box draws the search COLLAPSED: a
   *  real button carrying the look's glyph, and the box only once the host
   *  says it is `open`. Notion's view row is the case — the reference fills
   *  it with ~20px glyphs and ours put a 220px box among them (task 0187).
   *
   *  A BUTTON, never an inert mark: decision `0065` lets a look decide how a
   *  control is DRAWN and never whether a keyboard user has it, so the
   *  trigger is focusable and carries the same `data-ws-action` the sidebar's
   *  Search tool does — one route, two places to press it. */
  readonly trigger?: { readonly glyph: string; readonly label: string };
  /** Whether the box is showing. Meaningless without `trigger`: a search with
   *  no trigger has no collapsed state and its box is always there. */
  readonly open?: boolean;
  /** WHAT THIS SEARCH SEARCHES, for a screen reader — @Codex, P2 on
   *  `026c328`. The module was born as Obsidian's vault search and said
   *  "Search the vault" in every look that mounted it, including three that
   *  have no vault: Notion filters the board in front of you, Fibery the
   *  view, Fabric's composer asks the library. A reusable component does not
   *  get to name its host's subject, which is the same rule `placeholder`
   *  above already follows. The vault stays the default because the vault is
   *  where it came from and Obsidian declares nothing. */
  readonly label?: string;
}

/** Notion's nav sidebar: the workspace's name, then SECTIONS of rows the
 *  host resolves — the pool's upcoming dated objects, the workspace's
 *  spaces (its pools), each row an action the host routes. Nothing here is
 *  drawn that does not act (no dead controls in an imitation). */
export interface WorkspaceNavInput {
  /** The tools row's other icons the host cannot back (Notion's chat,
   *  calendar, inbox) — inert (0056), drawn after Home, before Search. */
  readonly inert?: readonly InertItem[];
  /** The window controls, drawn ABOVE the workspace switcher — where a
   *  reference that is a desktop window puts them. Declaration order is
   *  drawing order; the look says which controls and what colour. */
  readonly window?: readonly InertItem[];
  readonly workspace: string;
  readonly sections: readonly {
    readonly label: string;
    readonly rows: readonly {
      readonly label: string;
      readonly detail?: string;
      readonly icon?: string;
      readonly current?: boolean;
      /**
       * This row's categorical TONE — task 0178. A number naming which of a
       * look's colour slots the row occupies, emitted as `data-m-tone` so a
       * look maps its own palette across the slots in tokens.
       *
       * The point is what it REPLACES. Fibery picked a space's colour with
       * `:nth-of-type(6n+1)` through `(6n+6)` — the colour was a fact about
       * where the row sat. Six pools and a cycle of six meant it looked
       * right, which is why it survived; a seventh row, a hidden one, a
       * reorder, or two looks ordering one rail differently each break it,
       * the last being exactly what `0058` composition allows.
       *
       * Absent, the runtime derives one from the row's own `action`, which
       * is stable per item and independent of order — so a host that
       * declares nothing still gets a colour, and never a positional one.
       */
      readonly tone?: number;
      /** `pool:<id>` opens a pool; `object:<handle>` opens an object;
       *  `kept:<id>` reopens a starred page from the hub's shelf. */
      readonly action: string;
      /** The pages INSIDE this row, disclosed by a twisty — the app's own
       *  sidebar tree (Notion's teamspaces open to their pages). Drawn only
       *  when the host supplies them, and whether this row is open right now
       *  is view state under `fold`, never the spec's. */
      readonly children?: readonly {
        readonly label: string;
        readonly icon?: string;
        readonly action: string;
        readonly current?: boolean;
      }[];
      /** The view-state key this row's disclosure folds under. */
      readonly fold?: string;
    }[];
  }[];
  /** The tools row carries Search when the host backs it — the view row's
   *  search box, focused (`data-ws-action="search"`). */
  readonly search?: boolean;
  /** A New page at the sidebar's foot, for a writer (`data-ws-action="new"`). */
  readonly create?: boolean;
  /** The sidebar FOLDS (Fibery's, Notion's): a view-state key the host
   *  paints from the same store as a panel's or a column's collapse. The
   *  nav carries it and a toggle at its head; the topbar (given the same
   *  key) draws the compact identity and the expand toggle when it is
   *  away. The thirty-third verdict: the reference's frame has the
   *  sidebar folded and the workspace's name in the header. */
  readonly fold?: string;
}
/** The topbar's page tab (icon, name) — the label channel names it. */
export interface WorkspaceTopbarInput {
  readonly title: string; readonly icon?: string;
  /** The page actions the host BACKS. Absent, the strip alone is drawn:
   *  nothing is drawn that does not act (0055). Back and forward are the
   *  places visited under the look, greyed with nowhere to go as the app
   *  greys them; `kept` is whether the page is on the hub's shelf (the
   *  star, lit); `leave` names the ··· menu's way out of the look. */
  readonly actions?: {
    readonly canGoBack: boolean; readonly canGoForward: boolean;
    /** Absent, no star is drawn: an app without one (Coda) draws none. */
    readonly kept?: boolean; readonly leave: string;
  };
  /** Chrome the app's title bar shows and the host cannot back — 0056
   *  (Word's AutoSave, undo and redo, the centred search, Sign in, the
   *  window controls), placed at the bar's start, centre or end. */
  readonly inert?: readonly InertItem[];
  /** The sidebar's fold key and the workspace's name: with the sidebar
   *  away, the bar's start carries the expand toggle and the identity. */
  readonly fold?: { readonly key: string; readonly workspace: string };
  /** `false`: no tab strip — no chevrons, no page tab, no + — because the
   *  app's header has none (Fibery's is the identity, then the end's
   *  chrome; the thirty-fourth verdict's first item, the "fragmented and
   *  taller" upper frame). The page's doors stay where the app keeps
   *  them: the view row's New and the ··· menu. */
  readonly strip?: false;
}
/** The page head: a round icon and the page's name — and, for a doc, what
 *  the doc's page owns: a cover (a CSS image), the breadcrumb above the
 *  title, and a paragraph under it. Each drawn only when given. */
export interface WorkspacePageheadInput {
  readonly title: string; readonly icon?: string;
  readonly cover?: string; readonly crumb?: string; readonly words?: string;
}
/** Buttons as BLOCKS in a doc (Coda): each one a host action, routed by
 *  `data-ws-action`; drawn only for the actions the host backs. */
export interface DocActionsInput {
  readonly actions: readonly { readonly label: string; readonly action: string }[];
  /** The heading over the table that follows — the reference's "View of
   *  Tasks", a fact the host states (the table IS a view of that pool). */
  readonly heading?: string;
  /** The table's working tools at the heading's right (Coda's filter,
   *  sort, options) — inert (0056), drawn only with a heading. */
  readonly inert?: readonly InertItem[];
}
/** The views the host can show the collection as, and which is showing. */
export interface ViewSwitcherInput {
  readonly views: readonly { readonly id: string; readonly label: string; readonly active: boolean }[];
  /** Whether the host will create on the New button (a reader has none). */
  readonly canCreate: boolean;
  /** The row's tools the host cannot back (Fibery's Cards, Fields, Filter,
   *  Sort, Color), drawn inert between the views and New — 0056. */
  readonly inert?: readonly InertItem[];
  /** A second row of chips under the tools (Fibery's filter strip), inert;
   *  each chip says something TRUE about the view the host asserts. */
  readonly strip?: readonly InertItem[];
  /** The mark the look's New button carries at its right — Notion's dropdown
   *  chevron. Inert (0056) and INSIDE the real button rather than beside it:
   *  the menu it opens in the app offers page templates this host does not
   *  have, and a second control that does nothing is what 0055 forbids. The
   *  button still creates. */
  readonly createMark?: string;
}

type CapabilityInputOf =
  | { readonly capability: 'workspace-nav'; readonly nav: WorkspaceNavInput }
  | { readonly capability: 'workspace-topbar'; readonly topbar: WorkspaceTopbarInput }
  | { readonly capability: 'workspace-pagehead'; readonly pagehead: WorkspacePageheadInput }
  | { readonly capability: 'view-switcher'; readonly views: ViewSwitcherInput }
  | { readonly capability: 'doc-actions'; readonly actions: DocActionsInput }
  | { readonly capability: 'doc-ribbon'; readonly ribbon: DocRibbonInput }
  | { readonly capability: 'workspace-tabs'; readonly tabs: WorkspaceTabsInput }
  | { readonly capability: 'workspace-window'; readonly window: WorkspaceWindowInput }
  | { readonly capability: 'workspace-ribbon'; readonly ribbon: WorkspaceRibbonInput }
  | { readonly capability: 'workspace-status'; readonly status: WorkspaceStatusInput }
  | { readonly capability: 'workspace-search'; readonly search: WorkspaceSearchInput }
  | { readonly capability: 'timeline'; readonly timeline: TimelineInput }
  | { readonly capability: 'force-graph'; readonly graph: ForceGraphInput }
  | { readonly capability: 'continuous-plot'; readonly plot: ContinuousPlotInput }
  | { readonly capability: 'document-flow'; readonly document: DocumentFlowInput }
  | { readonly capability: 'calendar'; readonly calendar: CalendarInput };

/** A capability node may be rendered once, or once per object when it appears
 * in a collection template. The nested map prevents one row's host input from
 * being repeated for every instance while retaining the simple root-node form. */
/**
 * CLASSES THE CAPABILITY'S ELEMENT ALSO WEARS — task `0237`, and the whole of
 * what makes a composed app VISIBLE rather than only structural.
 *
 * Every app-scoped rule in the sheet is keyed `.m-app-<name>`, and 138 of
 * app.fibery's 143 selectors are DESCENDANT selectors — measured, not assumed.
 * So a part wearing its donor look's class is painted by that look wherever it
 * sits, and a composed app carries one look's shell with another's sidebar
 * without a single new rule.
 *
 * ON THE INPUT, not inside each capability's payload, and applied once in
 * `renderCapability`. The first version put it on `WorkspaceNavInput` and
 * wrapped inside the nav's own renderer — which composes exactly one of the
 * sixteen capabilities, and would have wanted the same six lines fifteen more
 * times. It is a fact about the capability INSTANCE, not about a nav's
 * content, and writing it where that is true is what makes the other fifteen
 * free.
 *
 * Host-supplied and free-form on purpose: the renderer does not know what a
 * look is called and must not. It writes the classes it is given, sanitised,
 * and the host is the only thing that knows which look a part came from.
 */
export type CapabilityInput = CapabilityInputOf & {
  readonly wearing?: readonly string[];
};

export type CapabilityInputs = ReadonlyMap<
  number,
  CapabilityInput | ReadonlyMap<ObjectHandle, CapabilityInput>
>;

export function capabilityInputFor(
  inputs: CapabilityInputs | undefined, node: number, object: ObjectHandle | undefined,
): CapabilityInput | undefined {
  const entry = inputs?.get(node);
  if (!entry) return undefined;
  if ('capability' in entry) return entry;
  return object === undefined ? undefined : entry.get(object);
}

/**
 * Pre-rendered slot content: one entry per CHILD, in spec order, keyed by slot.
 *
 * The HOST renders it, because slot children are ordinary core primitives and
 * the module has no business knowing how a field is drawn. The module decides
 * only WHERE each goes — the division the schema's "modules contain core
 * primitives in turn" describes.
 *
 * **An array, not a joined string.** The first version concatenated the
 * children of a slot into one blob, which is the same mistake as the one it
 * was written to fix, one level down: the compiler had thrown away WHICH SLOT
 * a child came from, and this then threw away WHICH CHILD. A module handed
 * `header: "<div>A</div><div>B</div>"` cannot put a rule between them, number
 * them, or lay them out as two things, because by then they are one string.
 * Nothing needed it yet, which is exactly why it was worth fixing before
 * something did.
 */
export type SlotHTML = Readonly<Record<string, readonly string[]>>;

/**
 * The anchor a table of contents points at — minted in ONE place.
 *
 * A ToC needs a name and a target per section. The name was never the problem:
 * `compile.ts` derives `nameFrom` on a card from its title or subtitle
 * binding, so a section knows what it is called without anyone authoring a
 * word. The target was, because it would naturally be computed by whatever
 * builds the list and emitted by whatever draws the cards — two computations
 * that must agree, with nothing making them.
 *
 * That is the shape of four separate defects found in this repo this week, so
 * it is answered the way the last one was: `dismissCandidate` and
 * `candidateKey` were moved into one module precisely so a recorder and a
 * filter could not disagree about what an id is. Same here. One function, both
 * sides.
 *
 * **Positional, and deliberately.** `ids are deliberately kept out of markup`
 * — following a reference asks the store to traverse rather than reading a
 * target off the DOM — so this carries a node index and an ordinal and no
 * object identity at all. It is a position in a render, which is exactly what
 * a link to a section on this page needs to be.
 */
export const sectionAnchor = (node: number, ordinal: number): string =>
  `m-sec-${node}-${ordinal}`;

/**
 * A note's own id, minted the same way and for the same reason as a section's.
 *
 * Positional, like `sectionAnchor`: a note is a position in a render — the
 * apparatus entry for the Kth section of node N — and carries no object
 * identity, because ids are deliberately kept out of markup.
 *
 * Separate from `sectionAnchor` rather than reusing it, because the marker and
 * the back-link point in OPPOSITE directions and a single id cannot be both
 * ends of a round trip. Sharing the prefix would have made `#m-sec-6-2` mean
 * the section in one place and the note in the other, which is the drift these
 * functions exist to prevent.
 */
export const noteAnchor = (node: number, ordinal: number): string =>
  `m-note-${node}-${ordinal}`;

/** The shape `noteAnchor` produces, and nothing else. Same rule as
 *  `isSectionAnchor`, which the comment beneath states in full. */
export const isNoteAnchor = (v: unknown): v is string =>
  typeof v === 'string' && /^m-note-\d+-\d+$/.test(v);

/**
 * The notes a document will actually draw, in the order it will draw them.
 *
 * **One function, both sides.** The module numbers this list and the card
 * renderer numbers the marker that points into it; a note kept by one and
 * dropped by the other is an apparatus whose every marker is one entry off,
 * and it looks completely correct until you follow one. This repo has found
 * that defect four times under other names — a link and a target minted by two
 * computations that must agree, with nothing making them.
 *
 * Drops what it cannot draw and deduplicates what it could draw twice: two
 * notes sharing an id put two elements on the page answering to one `#hash`,
 * and two notes on one section put two markers where a reader expects one.
 *
 * **A note must belong to a section this document knows about.** Checking the
 * SHAPE of the anchor was not enough: a stale or simply wrong anchor is
 * syntactically perfect, and it produced a numbered note whose return link went
 * nowhere while the card renderer found no section to mark — half an apparatus,
 * which is the failure this whole function exists to prevent, arriving through
 * the one door it was not watching. `DocumentFlowInput` is the public host
 * boundary and these values are untrusted by design. Found by @Codex.
 */
export function drawnNotes(input: DocumentFlowInput | undefined): readonly DocumentNote[] {
  const seenNote = new Set<string>();
  const seenSection = new Set<string>();
  const sections = new Set((input?.sections ?? []).map((s) => s.anchor));
  const out: DocumentNote[] = [];
  for (const n of input?.notes ?? []) {
    if (!isNoteAnchor(n.note) || !isSectionAnchor(n.anchor)) continue;
    if (!sections.has(n.anchor)) continue;
    if (seenNote.has(n.note) || seenSection.has(n.anchor)) continue;
    seenNote.add(n.note);
    seenSection.add(n.anchor);
    out.push(n);
  }
  return out;
}

/** The shape `sectionAnchor` produces, and nothing else.
 *
 *  An anchor reaches the module as DATA — the host computes it — and data is
 *  where an untrusted value arrives. Escaping it would be enough to keep the
 *  markup well-formed and would still let a caller point a contents entry at
 *  something that is not a section. Refusing anything but the minted shape is
 *  the same rule `mediaURL` follows for a scheme. */
export const isSectionAnchor = (v: unknown): v is string =>
  typeof v === 'string' && /^m-sec-\d+-\d+$/.test(v);

/**
 * WHICH node the sections of a document are, so a contents entry and a section
 * cannot be minted from two different collections.
 *
 * `sectionAnchor` is shared, and sharing it was not enough. The host built its
 * entries from `ir.nodes.find((n) => n.kind === 'collection')` — the FIRST
 * collection in the IR — and `withShell` wraps every painted lens in a panel
 * that contains one. So the contents listed node 6 while the sections rendered
 * as node 10, and every entry resolved to a card in the shell's list instead
 * of a section of the document. It had never once pointed at a section.
 *
 * The gate did not catch it because it asserted that each href resolved to *an
 * element*, which was true. Resolving to the RIGHT element is the assertion
 * that was missing, and it is in `e2e/document-flow.mjs` now.
 *
 * So the lookup lives here, beside the anchor it feeds, and it descends from
 * the capability node rather than searching the whole IR: the sections of a
 * document are the items of the collection inside that document, and no other
 * collection on the page can answer to that description.
 */
export function documentSectionTemplate(ir: IR, capability: number): number | undefined {
  const seen = new Set<number>();
  const walk = (i: number): number | undefined => {
    if (seen.has(i)) return undefined;
    seen.add(i);
    const n = ir.nodes[i];
    if (!n) return undefined;
    if (n.kind === 'collection') return n.itemTemplates?.[0]?.root;
    for (const c of n.children) {
      const hit = walk(c);
      if (hit !== undefined) return hit;
    }
    return undefined;
  };
  const root = ir.nodes[capability];
  if (!root || root.kind !== 'capability') return undefined;
  /* THE BODY SLOT, not the first collection anywhere under the capability —
     @Codex's P2 on `5fe2d35`. `document-flow` declares four slots (`body`,
     `header`, `footer`, `footnotes`), and this walked the capability's
     children in order and took the first collection it met. A running head
     built as a collection therefore answered for the document's SECTIONS:
     `documentSections()` minted every contents entry and note anchor against
     the header's item template while the body's cards rendered under a
     different one.

     Reproduced before the fix — a valid spec with a collection in `header`
     and the sections in `body`:

         slots of cap children  [ {slot: header, id: runners},
                                  {slot: body,   id: sections} ]
         returned template's parent   header, "runners"

     The comment above says *"no other collection on the page can answer to
     that description"*. That was true of the page and never of the CAPABILITY
     once it had slots — a quantifier the comment got right about the wrong
     scope, which is the shape `CLAUDE.md` files under reading a comment as a
     claim.

     THE BODY SLOT ALONE, with no fallback, and the first version had one.
     I wrote `bodyFirst` then a slotless fallback "so a shape that names no
     slot keeps working" — and the substitution proving it could not fire.
     `compile.ts` builds a capability's children ONLY from `node.slots`, and
     pushes a `slotOf` entry for every one, so a direct child of a capability
     is always slotted and the fallback was unreachable. Removed rather than
     kept as harmless: dead defence whose control cannot fire is what this
     repository files as measuring nothing, and it would have quietly
     reintroduced the header case the day a slotless child became possible.

     A `document-flow` with no `body` slot therefore answers `undefined`,
     which is the right answer: a document with no body has no sections, and
     minting its contents against the running head is the defect. */
  const bodyFirst = root.children.filter((c) => ir.nodes[c]?.slot === 'body');
  for (const c of bodyFirst) {
    const hit = walk(c);
    if (hit !== undefined) return hit;
  }
  return undefined;
}

export type CapabilityRenderer = (
  node: IRNode, input: CapabilityInput | undefined, slots: SlotHTML,
) => string;
type Renderer = CapabilityRenderer;

/** What a host supplies alongside a descriptor. `consumesSlots` is declared
 *  rather than inferred because `unconsumedSlots` reports what nothing will
 *  draw, and a renderer that quietly ate a slot would make that report lie. */
export interface CapabilityImplementation {
  readonly render: CapabilityRenderer;
  readonly consumesSlots: readonly string[];
}

const renderers = new Map<string, CapabilityRenderer>();

const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const graph: CapabilityRenderer = (node, supplied) => {
  const edge = node.channels?.['edge'];
  if (!edge || !edge.role.startsWith('relation.')) {
    return '<div class="m-capability m-capability-refused" role="note">'
      + 'Graph view needs a relation property. This pool has none.</div>';
  }
  if (!supplied || supplied.capability !== 'force-graph') {
    return '<div class="m-capability m-capability-refused" role="note">'
      + 'Graph data is unavailable for this view.</div>';
  }
  const input = supplied.graph;
  if (input.truncated || input.nodes.length > FORCE_GRAPH_LIMIT) {
    return `<div class="m-capability m-capability-refused" role="note">`
      + `Graph view supports up to ${FORCE_GRAPH_LIMIT} objects at once. `
      + 'Narrow the pool to open this graph.</div>';
  }
  if (input.edges.length > FORCE_GRAPH_EDGE_LIMIT) {
    return `<div class="m-capability m-capability-refused" role="note">`
      + `Graph view supports up to ${FORCE_GRAPH_EDGE_LIMIT} relations at once. `
      + 'Narrow the pool to open this graph.</div>';
  }
  // A LOCAL graph of a note with no links yet is one dot, and one dot is a
  // true picture of it (@Codex, P2 on 2c1ead8: a new note's Graph tab
  // opened a refusal). The page graph keeps refusing an edgeless pool: a
  // page of unconnected dots says nothing a table does not.
  const pane = node.config?.['pane'] === true;
  // THE VAULT GRAPH (`config.vault`; 0055, the tenth verdict's Graph view):
  // the whole vault as a canvas — every note a dot sized by its degree, the
  // label there for hover, focus and the open note, faint edges, the
  // column's box. One dot is a true picture of a one-note vault, as in the
  // pane.
  const vault = node.config?.['vault'] === true;
  if (input.edges.length === 0 && !pane && !vault) {
    return '<div class="m-capability m-capability-refused" role="note">'
      + 'This pool has a relation property, but no relations to draw.</div>';
  }

  const handles = new Set(input.nodes.map((item) => item.handle));
  const finite = (p: GraphPoint | undefined): p is GraphPoint =>
    !!p && Number.isFinite(p.x) && Number.isFinite(p.y);
  const complete = handles.size === input.nodes.length
    && input.nodes.every((item) => finite(input.positions.get(item.handle)))
    && input.edges.every((e) =>
      handles.has(e.from) && handles.has(e.to)
      && finite(input.positions.get(e.from)) && finite(input.positions.get(e.to)));
  if (!complete) {
    return '<div class="m-capability m-capability-refused" role="note">'
      + 'Graph data is unavailable for this view.</div>';
  }

  const lines = input.edges.map((e) => {
    const a = input.positions.get(e.from);
    const b = input.positions.get(e.to);
    return `<line x1="${a!.x.toFixed(1)}" y1="${a!.y.toFixed(1)}" x2="${b!.x.toFixed(1)}" y2="${b!.y.toFixed(1)}"/>`;
  }).join('');
  const showLabels = node.config?.['showLabels'] !== false;
  const labels = new Map(input.nodes.map((item) => [item.handle, item.label] as const));
  /**
   * IN A PANE — `config.pane` (the app template asks; 0055). Obsidian's
   * local graph is dots on the pane's ground with small labels under them
   * and the open note lifted; the page graph's 136×40 labelled buttons are
   * the wrong component at pane scale. The dot and the label are one
   * button, so the node is exactly as operable as on the page.
   */
  const box = pane ? FORCE_GRAPH_PANE_BOX : vault ? FORCE_GRAPH_VAULT_BOX : { width: 1000, height: 560 };
  const degree = new Map<string, number>();
  for (const e of input.edges) {
    degree.set(e.from, (degree.get(e.from) ?? 0) + 1);
    degree.set(e.to, (degree.get(e.to) ?? 0) + 1);
  }
  const nodes = input.nodes.map((item) => {
    const p = input.positions.get(item.handle)!;
    const attrs = anchorAttrs(item.handle, node.i, input.epoch);
    const current = item.handle === input.current ? ' aria-current="true"' : '';
    if (vault) {
      // The label is always in the markup — it is the button's name for a
      // screen reader either way — and the stylesheet shows it on hover,
      // focus and the open note. `--deg` sizes the dot.
      const d = Math.min(degree.get(item.handle) ?? 0, 8);
      return `<foreignObject x="${(p.x - 70).toFixed(1)}" y="${(p.y - 12).toFixed(1)}" width="140" height="44">`
        + `<button xmlns="http://www.w3.org/1999/xhtml" class="m-graph-node m-graph-dot" type="button"`
        + ` style="--deg:${d}"${attrs}${current} aria-label="Open ${esc(item.label)}" data-degree="${d}">`
        + `<span class="m-graph-dot-mark" aria-hidden="true"></span>`
        + `<span class="m-graph-dot-label">${esc(item.label)}</span></button></foreignObject>`;
    }
    if (pane) {
      const visible = showLabels ? `<span class="m-graph-dot-label">${esc(item.label)}</span>` : '';
      return `<foreignObject x="${(p.x - 70).toFixed(1)}" y="${(p.y - 6).toFixed(1)}" width="140" height="34">`
        + `<button xmlns="http://www.w3.org/1999/xhtml" class="m-graph-node m-graph-dot" type="button"`
        + `${attrs}${current} aria-label="Open ${esc(item.label)}"><span class="m-graph-dot-mark" aria-hidden="true"></span>${visible}</button></foreignObject>`;
    }
    const visible = showLabels ? esc(item.label) : '<span aria-hidden="true">•</span>';
    return `<foreignObject x="${(p.x - 68).toFixed(1)}" y="${(p.y - 18).toFixed(1)}" width="136" height="40">`
      + `<button xmlns="http://www.w3.org/1999/xhtml" class="m-graph-node" type="button"`
      + `${attrs}${current} aria-label="Open ${esc(item.label)}">${visible}</button></foreignObject>`;
  }).join('');
  const relationships = input.edges.map((edge) =>
    `<li>${esc(labels.get(edge.from))} linked to ${esc(labels.get(edge.to))}</li>`).join('');

  return `<figure class="m-force-graph${pane ? ' m-force-graph-pane' : ''}${vault ? ' m-force-graph-vault' : ''}">`
    + `<svg width="${box.width}" height="${box.height}" viewBox="0 0 ${box.width} ${box.height}"`
    + `${vault ? ' preserveAspectRatio="xMidYMid meet"' : ''} role="group" aria-label="Relationship graph, `
    + `${input.nodes.length} objects and ${input.edges.length} links">`
    + `<g class="m-graph-edges" aria-hidden="true">${lines}</g>${nodes}</svg>`
    + `<ul class="m-graph-relations" aria-label="Graph relationships">${relationships}</ul>`
    + `<figcaption>${input.nodes.length} objects · ${input.edges.length} relations</figcaption></figure>`;
};

/**
 * How many observations one plot will draw.
 *
 * **Measured, and lowered from 2000 because of what the measurement said.**
 * `node e2e/plot-scale.mjs` renders this same path at tiers into a real page
 * and counts dropped frames against the machine's own refresh interval, which
 * is `frame-time.mjs`'s method and deliberately not a second one.
 *
 * Chromium 1400×1000, 60Hz, **medians of five runs per tier**, dropped frames
 * beyond the one every insertion is entitled to. Measured on the OVERLAY
 * architecture that ships — a mark in the SVG and an absolutely-positioned HTML
 * button beside it, per observation:
 *
 * ```
 *      n   point   line   area        (bar refuses long before any of these)
 *    500       0      0      0
 *   1000       0      0      0
 *   1500       1      0      0
 *   2000       2      1      1
 *   3000       3      2      2
 * ```
 *
 * **`point` is the worst mark and it turns between 1000 and 1500**, so 1000 is
 * where this sits. A scatter emits one mark per observation while a line emits
 * one path for all of them; both emit one control each, which is why the marks
 * differ less than they would if geometry were the whole cost.
 *
 * `bar` never reaches these tiers: its band has to stay wider than a control,
 * so it refuses in the dozens. Its bound is interaction, not frames.
 *
 * The architecture matters to this table and the table was re-run because of
 * it. The first measurement was taken when each control was a `foreignObject`
 * — an independent layout root inside SVG — and moving the controls into an
 * HTML overlay changed the cost model the number had been derived from. @Codex
 * caught that the recorded evidence no longer described the code; the tiers
 * above are the re-run, and the turn did not move.
 *
 * The cost is mostly not the renderer — building the string at 2000 is ~5ms of
 * ~30 — but it is not nothing, and it runs on the same thread in the same
 * frame, so the harness builds AND inserts inside one watched frame. Timing
 * only the insertion, as the first version did, left that ~5ms outside the
 * verdict.
 *
 * Two corrections worth keeping, both @Codex's. An earlier comment cited a
 * `plot-scale.test.ts` that never existed. Then this one claimed
 * `node e2e/plot-scale.mjs` reproduced its table while the tiers above the
 * ceiling all printed REFUSED — the evidence path broken by the very change the
 * evidence justified. `measureWith` is why the command reproduces the table
 * now, and `pnpm e2e:plot-scale` runs every mark by default.
 *
 * What the number buys, whatever its value: above it the plot REFUSES rather
 * than sampling. A scatter of the first 500 of 40,000 observations, captioned
 * as though it were the set, is a lie a chart tells particularly well.
 */
export const PLOT_LIMIT = 1000;

/**
 * The ceiling actually in force, and the budget shared across one render pass.
 *
 * Two problems, one mechanism, both found by @Codex.
 *
 * **The evidence path broke when the evidence landed.** Lowering the ceiling to
 * 1000 made `e2e/plot-scale.mjs` refuse every tier above it, so the harness
 * printed `REFUSED` for exactly the rows — 1200, 1500, 2000 — that justify the
 * number, and the comment above claiming it reproduces the table was false.
 * That is the same defect as the `plot-scale.test.ts` that never existed, and I
 * wrote it while fixing that one. `measureWith` is the narrow, named way for a
 * benchmark to raise the ceiling; nothing in `apps/` or `packages/` calls it,
 * which `continuous-plot.test.ts` asserts by searching the source.
 *
 * **The ceiling was per plot, not per screen.** `renderCapability` runs once per
 * displayed object, so a collection template holding a plot could materialise
 * sixty of them — sixty thousand marks and sixty thousand controls — with every
 * single one inside its limit. A budget that resets per render pass is what
 * makes the measured number mean what it says.
 */
let ceiling = PLOT_LIMIT;
let spent = 0;

/** Start a render pass. Called by `renderHTML` before it walks the IR, so the
 *  budget below is per SCREEN rather than per plot. */
export function resetCapabilityBudget(): void { spent = 0; }

/**
 * Run `fn` with a different ceiling, for MEASUREMENT ONLY.
 *
 * `e2e/plot-scale.mjs` is the only caller and exists to find where the browser
 * stops keeping up. A benchmark that could not exceed the limit it is measuring
 * could not measure it. Restores the real ceiling even if `fn` throws, so a
 * failed measurement cannot leave the product with a raised limit.
 */
export function measureWith<T>(limit: number, fn: () => T): T {
  const was = ceiling;
  ceiling = limit;
  try { return fn(); } finally { ceiling = was; }
}

/** How many series the palette can tell apart. Six colours, so a seventh series
 *  would repeat the first — which the legend would then label as two things. */
export const PLOT_SERIES = 6;

/**
 * The narrowest the chart frame is allowed to render.
 *
 * The controls are a fixed 24 CSS pixels while the geometry scales with the
 * frame, which is what keeps the target floor honest at every width — but it
 * also means the SPACING between controls shrinks while their size does not.
 * Without a floor on the frame there is no worst case to design against, and
 * the bar refusal above has nothing decidable to compare.
 *
 * Below this the figure scrolls rather than scaling further, which is the same
 * answer a table gives a narrow screen. The stylesheet enforces it; the number
 * lives here because the refusal is computed from it.
 */
export const MIN_FRAME_PX = 600;

/** Marks this slice draws. `arc` is in the vocabulary's poolable config and is
 *  deliberately not here: a pie chart is a different projection, not a mark
 *  style, and drawing a line when a spec asked for an arc would be worse than
 *  saying so. */
const MARKS = new Set(['point', 'line', 'bar', 'area']);

/** Kinds an axis can carry. A plot needs a magnitude or a moment; a title on an
 *  axis is a category chart, which is a different capability.
 *
 *  Exported because a host has to CHOOSE the roles it binds to the axes, and a
 *  host that chose from its own copy of this list would be the drift task 0042
 *  names one layer up: the binding would validate against one table and be
 *  refused against another. It belongs in `CAPABILITY_CHANNEL_ROLE_KINDS`, and
 *  decision 0016 makes `@morph/spec` the authoritative home for exactly this
 *  kind of legality table — @Codex is carrying that move with the relation
 *  mutations. Until it lands: one table with two readers, rather than two
 *  tables. */
export const PLOT_AXIS_KINDS: readonly string[] = ['number', 'score', 'date'];
const AXIS_KINDS = new Set(PLOT_AXIS_KINDS);

const refuse = (why: string): string =>
  `<div class="m-capability m-capability-refused" role="note">${esc(why)}</div>`;

const plot: Renderer = (node, supplied) => {
  const x = node.channels?.['x'];
  const y = node.channels?.['y'];
  if (!x || !y) {
    return refuse('A plot needs an x and a y property. This pool has no pair to plot.');
  }
  // Enforced here rather than in the vocabulary's channel table, which is empty
  // for this capability and is being edited on another branch. That makes this
  // a weaker place for the contract to live than `CAPABILITY_CHANNEL_ROLE_KINDS`
  // — the spec would then refuse the binding before anything rendered — and
  // task 0043 records the move.
  for (const [name, channel] of [['x', x], ['y', y]] as const) {
    if (!AXIS_KINDS.has(ROLE_KIND[channel.role as Role])) {
      return refuse(`The ${name} axis needs a number, score or date property.`);
    }
  }
  const series = node.channels?.['series'];
  if (series && ROLE_KIND[series.role as Role] !== 'enum') {
    return refuse('A plot separates series by a property with a small set of values.');
  }

  const mark = String(node.config?.['mark'] ?? 'point');
  if (!MARKS.has(mark)) {
    return refuse(`${mark} marks are not implemented yet.`);
  }
  // The declared scales, honoured or named. `scaleX: 'time'` IS this geometry —
  // positioning epoch milliseconds linearly is what a time scale does — but
  // `ordinal` and `log` are different projections, and drawing linear geometry
  // for a spec that asked for a log axis silently changes the relationship the
  // chart communicates. Same rule as `arc`: say so rather than substitute.
  // Found by @Codex.
  const scaleX = String(node.config?.['scaleX'] ?? 'linear');
  const scaleY = String(node.config?.['scaleY'] ?? 'linear');
  if (scaleX !== 'linear' && scaleX !== 'time') {
    return refuse(`A ${scaleX} x scale is not implemented yet.`);
  }
  if (scaleY !== 'linear') {
    return refuse(`A ${scaleY} y scale is not implemented yet.`);
  }
  // `stacked` is the third piece of legal config that was read by nobody, and
  // the reason it is refused rather than implemented is worth stating: a stack
  // is a part-to-whole reading, and it only means anything when several series
  // share an x POSITION. This renderer's x is continuous — stacking it would
  // have to decide that two floats are the same category, and a stack that
  // groups the wrong observations draws a total that was never in the data.
  // That is the failure this file opens by naming. An ordinal x is what makes
  // the grouping exact, and `scaleX: 'ordinal'` is refused three lines up, so
  // the honest answer is to refuse until that lands. Found by @Codex.
  if (node.config?.['stacked'] === true) {
    return refuse('Stacked series need an ordinal x axis, which is not implemented yet.');
  }

  if (!supplied || supplied.capability !== 'continuous-plot') {
    return refuse('Plot data is unavailable for this view.');
  }
  const input = supplied.plot;
  if (input.truncated || input.points.length > ceiling) {
    return refuse(`A plot draws up to ${ceiling} observations at once. `
      + 'Narrow the pool to plot this.');
  }
  // The budget is for the SCREEN, not for this plot. Sixty plots of a thousand
  // points each are sixty thousand marks and sixty thousand controls, and every
  // one of them passed the check above. The number was measured for one chart;
  // this is what makes it mean one chart's worth. Found by @Codex.
  if (spent + input.points.length > ceiling) {
    return refuse(`This view already draws ${spent} observations, `
      + `and a screen draws up to ${ceiling}.`);
  }
  // Charged at the END of this function, not here. A plot that is refused
  // further down — seven series, a date on an extent axis, bars too narrow —
  // draws nothing, and charging it would refuse the next plot for room the
  // first one never took. Found by @Codex.
  if (input.points.length === 0) {
    return refuse('Nothing in this pool has both values to plot.');
  }
  if (!input.points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) {
    return refuse('Plot data is unavailable for this view.');
  }

  const W = 1000, H = 480, PAD = 56;
  const xs = input.points.map((p) => p.x);
  const ys = input.points.map((p) => p.y);
  // A mark whose meaning is EXTENT has to measure from zero. With a domain of
  // [min, max] the smallest value gets zero extent and 100 vs 101 renders as
  // nothing vs everything — the truncated-axis lie, drawn by the marks that
  // cannot survive it. Points and lines carry their meaning in position, so
  // they keep the tight domain that makes differences visible.
  //
  // Bars were fixed first and area was left behind, which is the same bug in
  // the other mark that fills to a baseline: a positive-only area closed to the
  // clamped data edge, so 90 beside 100 drew as nothing beside everything.
  // Both found by @Codex, one review apart.
  const bar = mark === 'bar';
  const extent = bar || mark === 'area';
  // A mark that measures from zero needs a y whose zero MEANS something. A date
  // has no zero magnitude — extending its domain to 0 measures every bar from
  // 1 January 1970, so 2020 and 2021 become two near-identical fifty-year
  // extents and the axis label reads a bare "0". Position is meaningful for a
  // date; length is not. Found by @Codex.
  if (extent && ROLE_KIND[y.role as Role] === 'date') {
    return refuse(`A ${mark} measures from zero, and a date has no zero. `
      + 'Plot a date against position instead.');
  }

  // The gap between adjacent distinct x values, in DATA units — the width one
  // bar is entitled to. Computed before the domain rather than after it,
  // because the domain has to make room for it: mapping the smallest and
  // largest x straight onto the axis endpoints and then centring a full-width
  // bar on each puts half of both bars outside the frame. With x = [0, 100]
  // that is a 710px bar starting at -299px. Found by @Codex.
  const distinctX = [...new Set(xs)].sort((a, b) => a - b);
  const gapData = distinctX.length > 1
    ? Math.min(...distinctX.slice(1).map((v, j) => v - distinctX[j]!))
    : 0;
  // Half a band at each end, for bars only: points and lines mark a position
  // and want the tight domain that makes differences visible.
  const padX = bar ? gapData / 2 : 0;

  const lo = { x: Math.min(...xs) - padX, y: extent ? Math.min(0, ...ys) : Math.min(...ys) };
  const hi = { x: Math.max(...xs) + padX, y: extent ? Math.max(0, ...ys) : Math.max(...ys) };
  // `b - a` can overflow to Infinity for finite endpoints of opposite huge
  // magnitude, and `Infinity / Infinity` is NaN — so every coordinate becomes
  // `cx="NaN"`, the marks vanish, and the finite-input check above has already
  // passed. Nothing is drawn and nothing is said, which is the one outcome this
  // renderer treats as worse than either. Found by @Codex.
  if (![hi.x - lo.x, hi.y - lo.y].every(Number.isFinite)) {
    return refuse('These values span too wide a range to plot.');
  }
  // A single distinct value has no extent. Dividing by a widened span still
  // yields 0, which pins every mark to the axis — a column of observations
  // drawn along the frame edge, which reads as a rendering fault rather than as
  // data. When there is no extent the honest position is the middle, so a
  // degenerate axis centres instead of collapsing to the origin. Caught by the
  // test that measures where a single observation lands.
  const norm = (v: number, a: number, b: number): number =>
    b === a ? 0.5 : (v - a) / (b - a);
  const px = (v: number): number => PAD + norm(v, lo.x, hi.x) * (W - PAD * 2);
  const py = (v: number): number => H - PAD - norm(v, lo.y, hi.y) * (H - PAD * 2);

  const bySeries = new Map<string, PlotPoint[]>();
  for (const p of input.points) {
    const key = p.series ?? '';
    (bySeries.get(key) ?? bySeries.set(key, []).get(key)!).push(p);
  }

  const groups = [...bySeries.entries()];
  // What an object with no series value is called. The legend labelled it
  // "Other", which is an ordinary enum value — a pool containing a real
  // "Other" drew two differently coloured series under one name, and the
  // legend could then identify neither. There is no string a pool cannot also
  // contain, so the collision is refused rather than renamed around.
  // Found by @Codex.
  const UNSET = 'No value';
  if (bySeries.has('') && bySeries.has(UNSET)) {
    return refuse(`Some objects have no series value, and "${UNSET}" is already `
      + 'one of the values. Rename it to separate the two.');
  }
  // Colours cycle through six. A seventh series is drawn in the first series'
  // colour, and the legend then names two different things identically — a
  // chart that looks readable and is not. Also @Codex.
  if (groups.length > PLOT_SERIES) {
    return refuse(`A plot separates up to ${PLOT_SERIES} series. `
      + `This grouping has ${groups.length}.`);
  }
  const zero = py(Math.min(Math.max(0, lo.y), hi.y));

  /* ------------------------------------------------- bar geometry, if bars */

  // A bar's width came from the OBSERVATION COUNT, which is only right when the
  // x values are evenly spaced. On a continuous axis they need not be: with
  // x = [0, 1, 100] the first two centres sit about 9px apart while each bar is
  // drawn ~207px wide, so they overpaint and the data underneath is gone. The
  // width a bar may occupy is a fact about the GAPS between distinct positions,
  // not about how many there are. Found by @Codex.
  //
  // No floor on the slot, and that is deliberate: the `Math.max(2, …)` this had
  // was itself an overlap bug, because two centres closer than 2px then carried
  // 2px bars. A floor on a width whose whole job is to fit inside a gap can
  // only ever break the thing it was protecting. Also @Codex.
  const inner = W - PAD * 2;
  // Four fifths of the band, so adjacent bars are separated rather than fused
  // into one block. The BAND is the gap; the bar is what sits inside it, which
  // is why the domain is padded by half a band and the bar drawn at less.
  const slot = 0.8 * (distinctX.length > 1
    ? (gapData / (hi.x - lo.x)) * inner
    : inner * 0.5);
  const barW = slot / Math.max(1, groups.length);
  // A bar nobody can see, or can see and cannot hit.
  //
  // @Codex opened the deployed preview and found Films drawing two bars 0.6px
  // wide with their 24px controls overlapping by 23.4px: a pointer aimed at one
  // observation selected another. Both halves are the same fact — a bar chart
  // on a CONTINUOUS x is only honest when the values are spread evenly, and the
  // band is only as wide as the closest pair. Two films released weeks apart
  // inside a twenty-five-year span make every band that narrow.
  //
  // The threshold is `MIN_TARGET_PX` scaled by how far the frame can SHRINK.
  //
  // `barW` is in viewBox units and a control is 24 CSS pixels, and those are
  // only the same thing when the frame renders at its full 1000. Comparing them
  // directly accepted bars whose controls overlap once the chart scales down:
  // fourteen positions in two series give `barW ≈ 25.4` and pass, while in a
  // 400px frame the same controls sit about 10 CSS pixels apart. Found by
  // @Codex, and it is the geometry-scales-but-controls-do-not split biting from
  // the other side.
  //
  // `MIN_FRAME_PX` is what makes the comparison decidable at all: below it the
  // figure scrolls instead of scaling, so the worst case is a known factor
  // rather than an unknown one.
  if (bar && barW < MIN_TARGET_PX * (W / MIN_FRAME_PX)) {
    return refuse('These values sit too close together to draw as bars. '
      + 'Plot them as points or lines instead.');
  }
  const seriesIndex = new Map(groups.map(([key], i) => [key, i] as const));
  /** Where a bar for `p` starts. One function, so the rectangle and the control
   *  that operates it cannot disagree about where the bar is — they did. */
  const barX = (p: PlotPoint): number =>
    px(p.x) - slot / 2 + (seriesIndex.get(p.series ?? '') ?? 0) * barW;
  /** The centre of the thing a control operates: a dodged bar's own centre, or
   *  the mark's position for every other mark. */
  const hitX = (p: PlotPoint): number => (bar ? barX(p) + barW / 2 : px(p.x));

  // Two observations at one x WITHIN one series draw two identical rectangles,
  // one hiding the other, with two controls stacked on top. Dodging separates
  // series, not duplicates inside a series, so there is no honest width to give
  // these — say so rather than paint one over the other.
  if (bar) {
    for (const [key, points] of groups) {
      const at = new Set(points.map((p) => p.x));
      if (at.size !== points.length) {
        return refuse(key
          ? `Bars need one observation per position. "${key}" has more than one.`
          : 'Bars need one observation per position; this pool has more than one.');
      }
    }
  }

  const shapes = groups.map(([key, points], i) => {
    const ordered = [...points].sort((a, b) => a.x - b.x);
    const cls = ` class="m-plot-series m-plot-s${i % 6}"`;
    const series = key ? ` data-series="${esc(key)}"` : '';
    const dot = (p: PlotPoint): string =>
      `<circle${cls} data-mark="point" cx="${px(p.x).toFixed(1)}" `
      + `cy="${py(p.y).toFixed(1)}" r="4"${series}/>`;

    if (mark === 'line' || mark === 'area') {
      // One observation is `M x y` and nothing else — a path with no stroked
      // segment, which is invisible. It happens the moment data is split into
      // series, so it is the common case rather than the odd one. Also @Codex.
      //
      // A dot is an honest fallback for a LINE, whose meaning is position. It
      // is not one for an AREA, whose meaning is filled extent from zero — the
      // figure would still say `data-mark="area"` while that one series showed
      // its magnitude as a position instead, and the substitution is invisible.
      // Found by @Codex.
      //
      // So a lone area observation keeps the encoding it was asked for: a
      // column filled from zero to its value, one band wide. That is what an
      // area of one point IS, and it is why this is a fallback rather than a
      // refusal — refusing the whole chart because one series is thin would
      // hide the several that are not.
      if (ordered.length === 1) {
        const only = ordered[0]!;
        if (mark === 'line') return dot(only);
        const top = Math.min(py(only.y), zero);
        const tall = Math.max(1, Math.abs(py(only.y) - zero));
        // Clamped to the frame. An area domain is NOT padded — only bars get
        // the half-band — so a full slot centred on the smallest or largest x
        // hangs outside: with x values [0, 1, 2] and the singleton at 2, a
        // 355-wide column starting at 766 ends at 1122 in a 1000-unit frame and
        // the SVG clips most of the extent it just promised. Found by @Codex,
        // and it is the endpoint-overflow bug the bar padding fixed, arriving
        // through the mark that does not get padded.
        const wanted = px(only.x) - slot / 2;
        const from = Math.max(PAD, Math.min(wanted, W - PAD - slot));
        const wide = Math.min(slot, W - PAD * 2);
        return `<rect${cls} data-mark="area"${series} `
          + `x="${from.toFixed(1)}" y="${top.toFixed(1)}" `
          + `width="${wide.toFixed(1)}" height="${tall.toFixed(1)}"/>`;
      }
      const d = ordered.map((p, j) =>
        `${j === 0 ? 'M' : 'L'}${px(p.x).toFixed(1)} ${py(p.y).toFixed(1)}`).join('');
      const closed = mark === 'area'
        ? `${d}L${px(ordered.at(-1)!.x).toFixed(1)} ${zero.toFixed(1)}`
          + `L${px(ordered[0]!.x).toFixed(1)} ${zero.toFixed(1)}Z`
        : d;
      return `<path${cls} data-mark="${mark}" d="${closed}"${series}/>`;
    }
    if (mark === 'bar') {
      // Dodged, not stacked and not overlaid. Splitting by series and then
      // drawing every series at the same x and width paints the later ones over
      // the earlier ones while the legend claims both are there.
      return ordered.map((p) => {
        const y = Math.min(py(p.y), zero);
        const h = Math.abs(py(p.y) - zero);
        return `<rect${cls} data-mark="bar"${series} `
          + `x="${barX(p).toFixed(1)}" y="${y.toFixed(1)}" `
          + `width="${barW.toFixed(1)}" height="${Math.max(1, h).toFixed(1)}"/>`;
      }).join('');
    }
    return ordered.map(dot).join('');
  }).join('');

  // Every observation is reachable, whatever the mark. A path is one element
  // for many points, so the marks above cannot carry the affordance — these
  // do, and they are real buttons for the reason the graph's are: a button is
  // in the tab order and answers Enter without anyone implementing either.
  // MIN_TARGET_PX, not a literal. a11y.ts exports it precisely because "a floor
  // the layer producing the number cannot see is a floor that gets missed,
  // which is how a 21px edit target shipped" — and a capability renderer is
  // outside the IR check that enforces it elsewhere, so it has to honour the
  // floor itself. This shipped at 22. Found by @Codex.
  //
  // They are HTML in an overlay rather than `foreignObject`s inside the SVG,
  // and that is the fix for a defect @Codex found by opening the deployed
  // preview: the chart was a fixed 1000px inside an 895px column, so its right
  // edge — including the endpoint tick that says what the domain IS — sat under
  // the shell panel, and three controls failed `elementFromPoint`.
  //
  // Making the SVG responsive alone would not have fixed it. A `viewBox` scales
  // everything inside it, so 24 user units become 21 CSS pixels in a 900px
  // column and the target floor quietly fails again — the same defect this file
  // has already had twice, arriving by a third route. Geometry belongs in the
  // SVG because it should scale; a CONTROL should not, because a finger does
  // not. So the marks scale with the frame and the controls are positioned as
  // percentages of it at a fixed pixel size, which is exact: the SVG preserves
  // its aspect ratio, so a percentage maps to the same place at every width.
  //
  // When the series channel is BOUND, every control names its series — including
  // the objects that have no value for it. The renderer colours and groups those
  // under "No value", and the marks are aria-hidden, so a name that omitted it
  // left a screen-reader user unable to tell that an observation belonged to the
  // group they could see in the legend. Found by @Codex.
  const grouped = !!node.channels?.['series'];
  const named = (p: PlotPoint): string => [
    p.label,
    `${input.xLabel} ${p.xText ?? p.x}`,
    `${input.yLabel} ${p.yText ?? p.y}`,
    ...(grouped ? [p.series ?? UNSET] : []),
  ].join(', ');
  // Positioned by `hitX`, not by `px(p.x)`. When bars dodge, the rectangles move
  // and the controls did not: every series' button stayed centred on the shared
  // x, so they overlapped each other, pointer activation went to whichever was
  // painted last, and the visible half of a displaced bar had no control at all.
  // A control that does not sit on the thing it operates is worse than no
  // control, because it looks like one. Found by @Codex.
  //
  // In the MARKS' order, not the input's. The marks are emitted grouped by
  // series and sorted within each group, while these kept whatever order the
  // host supplied — so with interleaved series the visibly topmost point and
  // the topmost control could be different observations, and clicking the one
  // you can see opened the one you cannot. The split overlap guarantee above
  // says the pointer reaches the topmost mark; that is only true if the last
  // control painted belongs to the last mark painted. Found by @Codex.
  const painted = groups.flatMap(([, pts]) => [...pts].sort((a, b) => a.x - b.x));
  const hits = painted.map((p) =>
    `<button class="m-plot-hit" type="button" `
    + `style="left:${((hitX(p) / W) * 100).toFixed(3)}%;`
    + `top:${((py(p.y) / H) * 100).toFixed(3)}%"`
    /* NO EPOCH, rather than the literal `0` this carried — task `0182`. An
       epoch exists so a commit from a dead render pass cannot land; a constant
       one can never detect a dead pass, so it was a stale-render guard that
       could not fire wearing the attribute name of one that can. A plot hit is
       NAVIGATION, and decision `0062` gives that its own spelling: emit the
       handle, emit no epoch. */
    + `${anchorAttrs(p.handle, node.i, undefined)} aria-label="${esc(named(p))}"></button>`).join('');

  /* --------------------------------------------------------- the domains */

  // An axis normalised to its data and captioned with a role name says nothing
  // about how much ground it covers. Scores of 4.0 and 4.1 fill the frame
  // exactly as 0 and 100 do, under the same word "Score" — so the reader sees a
  // dramatic separation and cannot tell whether it is a rounding error or the
  // whole range. A chart that hides its domain is the most confident lie in the
  // file, because nothing about it looks wrong. Found by @Codex.
  //
  // The endpoint values, not a tick algorithm. Two honest numbers beat five
  // invented ones, and the FORMATTING is the host's: `xText`/`yText` already
  // carry it, so a date axis reads "1960-03-10" rather than an epoch. Where an
  // endpoint is not itself an observation — the zero a bar or area domain
  // includes — there is no host text and the number is written plainly.
  //
  // The extreme OBSERVATIONS, not the ends of the domain — and those are not
  // the same thing. A bar domain is padded by half a band, so `lo.x` and
  // `hi.x` match no observation, the host text lookup missed, and a date axis
  // printed `1575763200000` where the earlier repair had just made it print a
  // date. A padded endpoint is a layout device; the reader wants to know what
  // the data reaches. Found by @Codex.
  const extremeAt = (axis: 'x' | 'y', pick: 'min' | 'max'): PlotPoint => {
    const value = (p: PlotPoint) => (axis === 'x' ? p.x : p.y);
    return input.points.reduce((best, p) =>
      (pick === 'min' ? value(p) < value(best) : value(p) > value(best)) ? p : best);
  };
  const textOf = (axis: 'x' | 'y', p: PlotPoint): string =>
    (axis === 'x' ? p.xText : p.yText) ?? String(axis === 'x' ? p.x : p.y);
  /** The host's text for a VALUE rather than for a point, since an extent
   *  domain's endpoint — zero — need not be an observation at all. */
  const textAtValue = (axis: 'x' | 'y', v: number): string => {
    const at = input.points.find((p) => (axis === 'x' ? p.x : p.y) === v);
    return at ? textOf(axis, at) : String(v);
  };
  const tick = (x: number, y: number, anchor: string, v: string): string =>
    `<text class="m-plot-tick" x="${x.toFixed(1)}" y="${y.toFixed(1)}" `
    + `text-anchor="${anchor}">${esc(v)}</text>`;
  // A degenerate axis has one value, and writing it at both ends would claim a
  // range it does not have. One label, in the middle, where the mark is.
  const [xLo, xHi] = [extremeAt('x', 'min'), extremeAt('x', 'max')];
  const [yLo, yHi] = [extremeAt('y', 'min'), extremeAt('y', 'max')];
  // The observation extrema are right for a PADDED axis, where the domain ends
  // at a value nothing sits on. They are wrong for an EXTENT axis, where the
  // domain deliberately reaches zero: y = [90, 100] renders against [0, 100]
  // and labelling 90 and 100 describes a 90–100 scale while the geometry uses
  // 0–100. That is the truncated-axis lie told by the labels instead of the
  // bars, which is the one place it had not yet appeared in this file. So the
  // rule follows the reason each domain differs from its data: x is padded, y
  // is extended, and only the padded one wants its observations. Found by
  // @Codex.
  const yEnds: readonly [number, string][] = extent
    ? [[lo.y, textAtValue('y', lo.y)], [hi.y, textAtValue('y', hi.y)]]
    : [[yLo.y, textOf('y', yLo)], [hi.y === yHi.y ? yHi.y : hi.y, textOf('y', yHi)]];
  // Placed where the observation is, since that is what the label names. On an
  // unpadded axis that is the frame edge; on a padded one it is half a band in.
  const ticks = [
    ...(xLo.x === xHi.x
      ? [tick(W / 2, H - PAD + 18, 'middle', textOf('x', xLo))]
      : [tick(px(xLo.x), H - PAD + 18, 'start', textOf('x', xLo)),
         tick(px(xHi.x), H - PAD + 18, 'end', textOf('x', xHi))]),
    ...(yEnds[0]![0] === yEnds[1]![0]
      ? [tick(PAD - 8, H / 2, 'end', yEnds[0]![1])]
      : yEnds.map(([v, text]) => tick(PAD - 8, py(v) + 4, 'end', text))),
  ].join('');

  // A legend whenever any series is NAMED, not only when there are several. A
  // filtered pool with one series still colours its marks by that series, and
  // suppressing the key left the value affecting the picture while appearing
  // nowhere in it — a reader could see a colour and not what it meant. Found by
  // @Codex.
  // Where zero actually is, when that is not the bottom of the frame.
  //
  // Bars and areas measure from `zero`, and `zero` moves: with negative values
  // it sits inside the frame, and with an all-negative domain it sits at the
  // top. The horizontal axis stayed pinned to the bottom regardless, so the
  // bars hung from an invisible line while a visible one marked a different
  // value entirely — a reader would measure every bar from the wrong place.
  // Found by @Codex.
  //
  // Drawn only when it differs from the frame's own axis, so the ordinary
  // all-positive chart is unchanged rather than gaining a second line on top of
  // its first.
  const baseline = extent && Math.abs(zero - (H - PAD)) > 0.5
    ? `<line class="m-plot-zero" x1="${PAD}" y1="${zero.toFixed(1)}" `
      + `x2="${W - PAD}" y2="${zero.toFixed(1)}"/>`
    : '';

  const legend = [...bySeries.keys()].some(Boolean)
    ? `<figcaption class="m-plot-legend">${[...bySeries.keys()].map((k, i) =>
        `<span class="m-plot-key m-plot-s${i % 6}">${esc(k || UNSET)}</span>`).join('')
      }</figcaption>`
    : '';

  // Charged now that every refusal is behind us, so the budget counts what is
  // drawn rather than what was attempted.
  spent += input.points.length;

  return `<figure class="m-plot" data-mark="${mark}">`
    // The frame is what the controls are positioned inside, and it is the same
    // box the SVG occupies — so a percentage means the same thing to both.
    + `<div class="m-plot-frame" style="aspect-ratio:${W} / ${H}">`
    + `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="group" `
    + `aria-label="${esc(input.yLabel)} against ${esc(input.xLabel)}, `
    + `${input.points.length} observations">`
    + `<line class="m-plot-axis" x1="${PAD}" y1="${H - PAD}" x2="${W - PAD}" y2="${H - PAD}"/>`
    + `<line class="m-plot-axis" x1="${PAD}" y1="${PAD}" x2="${PAD}" y2="${H - PAD}"/>`
    + baseline
    + ticks
    + `<text class="m-plot-label" x="${W / 2}" y="${H - 14}" text-anchor="middle">`
    + `${esc(input.xLabel)}</text>`
    + `<text class="m-plot-label" x="16" y="${H / 2}" text-anchor="middle" `
    + `transform="rotate(-90 16 ${H / 2})">${esc(input.yLabel)}</text>`
    + `<g class="m-plot-marks" aria-hidden="true">${shapes}</g></svg>`
    + `${hits}</div>${legend}</figure>`;
};

/**
 * A document surface — the Word row's "document flow", and the first module
 * whose whole job is LAYOUT over core primitives rather than a drawing of its
 * own.
 *
 * It renders none of its own content. `body`, `header`, `footer` and
 * `footnotes` arrive as HTML the host produced from ordinary spec nodes, and
 * this decides only where each goes. That is what the schema means by "modules
 * contain core primitives in turn", and it is why the slot machinery had to
 * exist before this could — before it, a header and a footer reached the IR
 * indistinguishable and neither was drawn at all.
 *
 * ## What `paginated` does, and the half it deliberately does not do
 *
 * **In print it is real.** `@page` gives the sheet its size and margins, the
 * running head and foot repeat on every sheet because Chromium repeats
 * `position: fixed` boxes per printed page, and `break-inside: avoid` keeps a
 * card from being sawn in half. Verifiable: `e2e/document-flow.mjs` prints to
 * PDF and counts `/Type /Page`, so "this document is four pages" is a measured
 * claim rather than a hopeful one.
 *
 * **On screen it is not pagination.** It is a sheet — page-shaped, with the
 * margins and the running head and foot — and the text flows down it
 * continuously instead of breaking into numbered pages. Two reasons, and
 * neither is that it would be hard:
 *
 *  - CSS paged media exists only when printing. Splitting prose into page
 *    boxes on screen means measuring where the browser actually fragmented it
 *    and re-flowing into fixed-height containers — a layout loop that runs on
 *    every resize, and one with a real accessibility cost, since a reader
 *    moving through N absolutely-positioned boxes is not reading one document.
 *    That wants its own review, not a corner of this one.
 *  - Page numbers are a PRINT feature, and they work. This entry used to read
 *    *"Page NUMBERS are not available at all... Chromium does not implement
 *    those"*, which was true when it was written and is not on Chrome 151.
 *    `BASE_CSS` carries `@bottom-center{content:counter(page)}` inside its
 *    `@page` block now, and the browser counts the page — so the second half
 *    of the old objection, that a script-counted number *"would be wrong the
 *    moment anything reflowed"*, is moot rather than answered.
 *
 *    Measured differentially, because a PDF's text is font-encoded and cannot
 *    be read back from this sandbox: the same document printed with the box
 *    and with `content: none` differs in size, and a box with more in it grows
 *    the file further. A rule the parser discards emits no bytes and cannot
 *    scale with its content. `e2e/page-number.mjs` holds it.
 *
 *    **That is not evidence the number is legible, placed correctly, or says
 *    what it should** — only that the browser drew something for the box and
 *    that what it drew depends on what the box says. A person still has to
 *    look, which is what the bar asks for anyway.
 *
 *    On SCREEN there are still no numbered pages, for the reason above this:
 *    CSS paged media exists only when printing.
 */
const documentFlow: Renderer = (node, _input, slots) => {
  const cfg = (node.config ?? {}) as Record<string, unknown>;
  const columns = typeof cfg['columns'] === 'number'
    ? Math.max(1, Math.min(6, Math.trunc(cfg['columns'])))
    : 1;
  const paginated = cfg['paginated'] === true;
  // A footnotes REGION is drawn only when the spec asked for footnotes and the
  // host actually supplied some. Asking for them and getting an empty rule
  // above nothing is worse than a document with no apparatus.
  /**
   * The table of contents, built from what the HOST named.
   *
   * The module cannot read the sections out of `slots['body']` — by then they
   * are rendered HTML — so the host supplies `{ anchor, name }` per section and
   * mints each anchor with `sectionAnchor`, the same function the card renderer
   * uses. It "updates" for free: it is derived every render from the set the
   * lens is showing, so there is no stale copy to go wrong.
   *
   * Drawn only when there is more than one section. A contents list with one
   * entry is a heading with extra steps.
   */
  const sections = _input && _input.capability === 'document-flow'
    ? _input.document.sections : [];
  const toc = cfg['toc'] === true && sections.length > 1
    ? `<nav class="m-doc-toc" aria-label="Table of contents">`
      + `<h2 class="m-doc-toc-title">Contents</h2><ol>`
      + sections.filter((sec) => isSectionAnchor(sec.anchor)).map((sec) =>
          `<li><a href="#${esc(sec.anchor)}">${esc(sec.name)}</a></li>`).join('')
      + `</ol></nav>`
    : '';

  const wantsNotes = cfg['footnotes'] === true;
  // Each note is its own list item now that the children arrive separately —
  // which is the whole reason a slot carries an array. Concatenated, an
  // apparatus of four notes was one paragraph.
  const noteItems = wantsNotes ? (slots['footnotes'] ?? []) : [];
  /**
   * The DERIVED notes — one per section that cites something.
   *
   * Two sources, deliberately. A spec may fill the `footnotes` slot with
   * ordinary nodes and get them placed; a host may derive an apparatus from
   * what the sections actually reference. The slot is authored content and the
   * input is derived content, and a document can have both — so they are
   * concatenated into one `<ol>` and numbered continuously, because two
   * separately-numbered lists under one "Notes" heading is two apparatuses
   * pretending to be one.
   *
   * The anchor pair is what makes an entry a footnote rather than a paragraph
   * at the end: `id` is what the section's marker points at, and the back-link
   * is what returns. An entry whose anchors are not the minted shape is
   * dropped rather than escaped — see `DocumentNote`.
   */
  const derived = wantsNotes && _input && _input.capability === 'document-flow'
    ? drawnNotes(_input.document)
    : [];
  // Derived notes come FIRST, and that is a numbering decision rather than a
  // taste one. A marker in the body carries the note's number, and the card
  // renderer computes it from this list's index; if authored slot children sat
  // ahead of them, every marker would be off by however many the spec happened
  // to declare — a count the card renderer would have to reconstruct from the
  // IR, which is the second computation this file exists to avoid.
  const items = [
    ...derived.map((n) =>
      // NO `role="doc-endnote"`. DPUB-ARIA 1.1 deprecates it, and independently
      // of which version anyone is reading: a role on an `<li>` REPLACES its
      // `listitem` role, which throws away the programmatic list position —
      // the very numbering this apparatus just had to be repaired to draw.
      // The valid roles are the two on the links and `doc-endnotes` on the
      // region; the entries stay native list items. Found by @Codex.
      //
      // `tabindex="-1"` is what makes a fragment jump take the READER with it.
      // Without it the browser moves the viewport and leaves `activeElement` on
      // `<body>`: measured, both directions. A keyboard or screen-reader user
      // follows a marker and arrives nowhere.
      `<li class="m-doc-note" id="${esc(n.note)}" tabindex="-1">`
      + `<span class="m-doc-note-text">${esc(n.text)}</span>`
      + `<a class="m-doc-note-back" href="#${esc(n.anchor)}" role="doc-backlink"`
      + ` aria-label="Back to the text">\u21a9</a></li>`),
    ...noteItems.map((n) => `<li>${n}</li>`),
  ];
  const notes = items.length
    ? `<ol class="m-doc-note-list">${items.join('')}</ol>`
    : '';

  const join = (xs: readonly string[] | undefined): string => (xs ?? []).join('');
  const headHtml = join(slots['header']);
  const head = headHtml
    ? `<header class="m-doc-running m-doc-head" role="banner">${headHtml}</header>` : '';
  /* The RUNNING head on paper — task 0165. A fixed header repeats per printed
   * sheet, but Chromium paints it at the top of the content area, where each
   * page's first line also starts (measured: overlap), and clips anything
   * pushed up into the margin (measured: a negative top erased it). The one
   * per-page surface the browser paints INSIDE the margin is an `@page`
   * margin box — the page counter already lives in `@bottom-center` — and a
   * margin box takes only a string, so the head's text is lifted out of its
   * markup here and emitted as a per-document rule. The base sheet hides the
   * in-flow header in print; on screen this `<style>` is inert (`@page` is
   * print-only) and the header renders as the ordinary block it always was.
   * Entities are decoded because the slot content was `esc()`d on the way in,
   * and `&amp;` goes last so a literal `&lt;` in a title cannot double-decode. */
  const headText = headHtml
    .replace(/<[^>]*>/g, ' ')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ').trim();
  const runningRule = headText
    ? `<style>@page{@top-center{content:"${
        headText.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
      }";font:400 9pt system-ui,sans-serif;color:#666}}</style>`
    : '';
  const foot = join(slots['footer'])
    ? `<footer class="m-doc-running m-doc-foot" role="contentinfo">${join(slots['footer'])}</footer>` : '';
  const body = `<div class="m-doc-body"${columns > 1 ? ` style="column-count:${columns}"` : ''}>`
    + `${join(slots['body'])}</div>`;
  const apparatus = notes
    ? `<aside class="m-doc-notes" role="doc-endnotes"><h2 class="m-doc-notes-title">Notes</h2>`
      + `${notes}</aside>`
    : '';

  return `<article class="m-doc-flow${paginated ? ' m-doc-sheet' : ''}"`
       + ` data-columns="${columns}" data-paginated="${paginated}">`
       + `${runningRule}${head}${toc}${body}${apparatus}${foot}</article>`;
};

/* ------------------------------------------------------------------ calendar */

/** Days in a month, from a `YYYY-MM` string. Leap years included, because
 *  February is the month a hand-rolled table gets wrong. `Date.UTC` with day 0
 *  of the NEXT month is the last day of this one — arithmetic the platform
 *  already knows, rather than a lookup table plus a leap rule. */
const daysInMonth = (year: number, month1: number): number =>
  new Date(Date.UTC(year, month1, 0)).getUTCDate();

/** The weekday of `YYYY-MM-01`, 0 = Sunday. UTC throughout: a grid that starts
 *  on a different column depending on the reader's offset is not a calendar. */
const firstWeekday = (year: number, month1: number): number =>
  new Date(Date.UTC(year, month1 - 1, 1)).getUTCDay();

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** `YYYY-MM`, and nothing else. The month reaches this module as DATA — the
 *  host computes it — so it is checked rather than trusted, the same rule
 *  `isSectionAnchor` follows for an anchor and `mediaURL` for a scheme. */
const parseMonth = (v: unknown): { year: number; month1: number } | undefined => {
  if (typeof v !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(v)) return undefined;
  const year = Number(v.slice(0, 4));
  const month1 = Number(v.slice(5, 7));
  // A four-digit year is already bounded by the pattern; this rejects year 0000,
  // which parses fine and places a grid nobody asked for.
  return year > 0 ? { year, month1 } : undefined;
};

/**
 * A month grid, with what falls on each day.
 *
 * `ACCEPTANCE.md`'s Notion row asks for "databases with every view type", and a
 * calendar is one of the six Notion ships. It is the same operation a board
 * performs — put each object in the bucket a role names — with the buckets
 * fixed by the calendar rather than by the data, which is the only reason it is
 * a module and not a presentation: a board's columns come from the objects, a
 * month's do not.
 *
 * The grid is a `<table>` on purpose. A month IS tabular data — seven columns
 * of weekdays, one row per week — and a screen reader moving through a grid of
 * `<div>`s with `role="gridcell"` bolted on gets a worse version of what
 * `<th scope="col">` already provides for free.
 */
const calendar: Renderer = (node, supplied) => {
  const cfg = node.config ?? {};
  if (!supplied || supplied.capability !== 'calendar') {
    return '<div class="m-capability m-capability-refused" role="note">'
      + 'Calendar data is unavailable for this view.</div>';
  }
  const at = parseMonth(supplied.calendar.month);
  if (!at) {
    // A refusal rather than a fallback to "now". A grid drawn around a month
    // nobody asked for looks like a working calendar showing the wrong thing,
    // which is worse than one that says it has no month.
    return '<div class="m-capability m-capability-refused" role="note">'
      + 'This calendar was given no month to draw.</div>';
  }

  const { year, month1 } = at;
  const raw = cfg['weekStart'];
  const weekStart = typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw <= 6
    ? raw : 0;

  const byDay = new Map<string, string[]>();
  for (const e of supplied.calendar.events) {
    if (typeof e?.day !== 'string') continue;
    // A BUTTON, like a graph node, and for the same reason: the only reachable
    // thing on a calendar is its events, and a `<li>` carrying an anchor is
    // openable with a mouse and invisible to a keyboard.
    const attrs = e.handle === undefined
      ? '' : anchorAttrs(e.handle, node.i, supplied.calendar.epoch);
    (byDay.get(e.day) ?? byDay.set(e.day, []).get(e.day)!).push(
      `<li class="m-cal-event">`
      + `<button class="m-cal-hit" type="button"${attrs}>${esc(e.label)}</button></li>`);
  }

  const total = daysInMonth(year, month1);
  // How many blank cells before the 1st. `+ 7` because the difference is
  // negative whenever the month starts before the configured week start, and a
  // negative lead silently drops cells rather than shifting them.
  const lead = (firstWeekday(year, month1) - weekStart + 7) % 7;
  const cells: string[] = [];
  for (let i = 0; i < lead; i += 1) {
    cells.push('<td class="m-cal-pad"></td>');
  }
  const today = typeof supplied.calendar.today === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(supplied.calendar.today)
    ? supplied.calendar.today : undefined;
  for (let d = 1; d <= total; d += 1) {
    const iso = `${year}-${String(month1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const events = byDay.get(iso) ?? [];
    const list = events.length
      ? `<ul class="m-cal-events">${events.join('')}</ul>` : '';
    cells.push(
      `<td class="m-cal-day${events.length ? ' m-cal-has' : ''}${iso === today ? ' m-cal-today' : ''}" data-day="${iso}">`
      + `<time class="m-cal-num" datetime="${iso}">${d}</time>${list}</td>`);
  }
  // Trailing pad so the last row is a full week and the grid keeps its columns.
  while (cells.length % 7 !== 0) cells.push('<td class="m-cal-pad"></td>');

  const rows: string[] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(`<tr>${cells.slice(i, i + 7).join('')}</tr>`);
  }

  const heads = Array.from({ length: 7 }, (_, i) =>
    `<th scope="col"><abbr title="${DAY_NAMES[(weekStart + i) % 7]}day">`
    + `${DAY_NAMES[(weekStart + i) % 7]}</abbr></th>`).join('');

  const title = `${MONTH_NAMES[month1 - 1]} ${year}`;

  // What is NOT on this grid is said, never silently dropped. Two different
  // sentences, because "dated elsewhere" and "not dated" are two different
  // facts and a reader needs to tell them apart.
  const undated = supplied.calendar.undated ?? 0;
  const elsewhere = supplied.calendar.elsewhere ?? 0;
  const said: string[] = [];
  if (elsewhere > 0) {
    said.push(`${elsewhere} ${elsewhere === 1 ? 'object falls' : 'objects fall'} in other months`);
  }
  if (undated > 0) {
    said.push(`${undated} ${undated === 1 ? 'has' : 'have'} no date`);
  }
  const note = said.length
    ? `<p class="m-cal-undated" role="note">${esc(said.join('; '))}.</p>` : '';

  // A way OUT of this month, drawn only where there is somewhere to go. The
  // module renders the controls because it owns its own interior; the host
  // decides what they mean, which is why they carry a month rather than a
  // direction — an arrow that means "previous" has to agree with whatever the
  // host thinks the previous month is, and two computations that must agree is
  // the defect shape this repo keeps paying for.
  const months = (supplied.calendar.months ?? []).filter((m) => parseMonth(m));
  const here = months.indexOf(supplied.calendar.month);
  const step = (to: string | undefined, label: string, glyph: string): string =>
    to === undefined
      ? `<button class="m-cal-nav" type="button" disabled aria-label="${label}">${glyph}</button>`
      : `<button class="m-cal-nav" type="button" data-month="${esc(to)}"`
        + ` aria-label="${label}">${glyph}</button>`;
  // With the host's TODAY, the way out is the app's own: the month before,
  // Today, the month after — every month reachable, the host answering any.
  const adjacent = (delta: number): string => {
    const m = month1 - 1 + delta;
    const y = year + Math.floor(m / 12);
    return `${y}-${String(((m % 12) + 12) % 12 + 1).padStart(2, '0')}`;
  };
  const thisMonth = supplied.calendar.month;
  const nav = today !== undefined
    ? `<div class="m-cal-steps m-cal-steps-today">`
      + step(adjacent(-1), 'Previous month', '\u2039')
      + (today.slice(0, 7) === thisMonth
        ? `<button class="m-cal-nav m-cal-today-btn" type="button" disabled aria-label="Today">Today</button>`
        : `<button class="m-cal-nav m-cal-today-btn" type="button" data-month="${esc(today.slice(0, 7))}" aria-label="Today">Today</button>`)
      + step(adjacent(1), 'Next month', '\u203a')
      + `</div>`
    : months.length > 1 && here >= 0
      ? `<div class="m-cal-steps">`
        + step(months[here - 1], 'Earlier month with objects', '\u2190')
        + `<span class="m-cal-of">${here + 1} of ${months.length}</span>`
        + step(months[here + 1], 'Later month with objects', '\u2192')
        + `</div>`
      : '';

  return `<div class="m-capability m-calendar" data-month="${esc(supplied.calendar.month)}">`
    + `<div class="m-cal-head"><h2 class="m-cal-title">${esc(title)}</h2>${nav}</div>`
    // `aria-label` rather than a visually-hidden `<caption>`: this sheet has no
    // shared hidden-text class — `m-graph-relations` rolls its own — so a
    // caption would simply have printed the month twice, once under the
    // heading that already says it.
    + `<table class="m-cal-grid" aria-label="${esc(title)}">`
    + `<thead><tr>${heads}</tr></thead><tbody>${rows.join('')}</tbody></table>`
    + note + `</div>`;
};

renderers.set('document-flow@0.1.0', documentFlow);
renderers.set('calendar@0.1.0', calendar);

/* ------------------------------------------------------------------ timeline */

/** A fraction clamped into the axis. A bar whose host arithmetic ran off the
 *  end would otherwise be drawn outside the frame, where it reads as missing
 *  rather than as out of range. */
const onAxis = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0;

/**
 * A shared time axis, one lane per object.
 *
 * `ACCEPTANCE.md`'s Notion row asks for "databases with every view type", and
 * timeline is the sixth. It is the one that needed a vocabulary change to be
 * honest: a bar has two ends, and one `date` role cannot carry them — two
 * manifest keys binding the same role return an ARRAY, which is two values in
 * no particular order rather than a span. So `date.start` and `date.end` are
 * words now, and this draws what they say.
 *
 * Rows are a `<table>` for the same reason the calendar's grid is: a lane per
 * object with a shared axis is tabular, and a real row header gives a screen
 * reader the object's name against every bar without a single ARIA attribute.
 * The bar itself is positioned with percentages of its own cell — no absolute
 * pixel arithmetic, so it survives the column being any width.
 */
const timeline: Renderer = (node, supplied) => {
  const cfg = node.config ?? {};
  if (!supplied || supplied.capability !== 'timeline') {
    return '<div class="m-capability m-capability-refused" role="note">'
      + 'Timeline data is unavailable for this view.</div>';
  }
  const input = supplied.timeline;
  if (input.bars.length === 0) {
    // A refusal that says which of the two reasons it is. "No dates at all" and
    // "dates this lens cannot read" send a person to different places.
    const why = (input.undated ?? 0) > 0
      ? 'None of these objects has a date this timeline can place.'
      : 'This pool has no dates to lay out.';
    return `<div class="m-capability m-capability-refused" role="note">${esc(why)}</div>`;
  }

  const showToday = cfg['showToday'] === true;
  // Each LANE gets an index — the host's declared order first, then
  // first-seen — carried on its bars as `data-lane-index`, so a look can
  // colour bars by lane the way it tints the board's columns, and a status
  // is one colour in both. A lane-less bar carries none.
  const lanes: string[] = [...(input.lanes ?? [])];
  const laneIndex = (lane: string | undefined): number | undefined => {
    if (lane === undefined || lane === '') return undefined;
    const at = lanes.indexOf(lane);
    if (at >= 0) return at;
    lanes.push(lane);
    return lanes.length - 1;
  };
  const rows = input.bars.map((b) => {
    const from = onAxis(b.from);
    const to = Math.max(from, onAxis(b.to));
    // A MOMENT rather than a span. A zero-width rectangle is invisible, so an
    // object with one date would silently leave the chart it belongs on.
    const moment = to - from < 0.005;
    const attrs = b.handle === undefined
      ? '' : anchorAttrs(b.handle, node.i, input.epoch);
    const style = moment
      ? `left:${(from * 100).toFixed(2)}%`
      : `left:${(from * 100).toFixed(2)}%;width:${((to - from) * 100).toFixed(2)}%`;
    const lane = b.lane ? `<span class="m-tl-lane">${esc(b.lane)}</span>` : '';
    const open = b.open === true && !moment;
    const li = laneIndex(b.lane);
    return `<tr><th scope="row" class="m-tl-name">${esc(b.label)}${lane}</th>`
      + `<td class="m-tl-track">`
      + `<button class="m-tl-bar${moment ? ' m-tl-moment' : ''}`
      + `${open ? ' m-tl-open' : ''}" type="button"`
      + ` style="${style}"${li === undefined ? '' : ` data-lane-index="${li}"`}${attrs}`
      + ` aria-label="${esc(b.label)}${open ? ', still open' : ''}">`
      + `<span class="m-tl-hidden">${esc(b.label)}</span></button></td></tr>`;
  }).join('');

  const undated = input.undated ?? 0;
  const note = undated > 0
    ? `<p class="m-tl-undated" role="note">${undated} `
      + `${undated === 1 ? 'object has' : 'objects have'} no date and are not on the axis.</p>`
    : '';

  return `<div class="m-capability m-timeline"${showToday ? ' data-today="true"' : ''}>`
    + `<table class="m-tl-grid" aria-label="Timeline from ${esc(input.from)} `
    + `to ${esc(input.to)}">`
    + `<thead><tr><th scope="col" class="m-tl-corner">Object</th>`
    + `<th scope="col" class="m-tl-scale">`
    + `<span class="m-tl-from">${esc(input.from)}</span>`
    + `<span class="m-tl-to">${esc(input.to)}</span></th></tr></thead>`
    + `<tbody>${rows}</tbody></table>${note}</div>`;
};

renderers.set('timeline@0.1.0', timeline);


renderers.set('continuous-plot@0.1.0', plot);
renderers.set('force-graph@0.2.0', graph);

/* ---------------------------------------------- the workspace chrome (0055) */

/** One monochrome stroke icon, 18px, the way Obsidian's ribbon draws them.
 *
 *  `d` IS ESCAPED — @Codex's P2 on `ee1dd16`, and the finding is about what
 *  the previous commit changed rather than about this helper. Every caller
 *  used to pass a literal from this file; `WorkspaceRibbonInput.tools` made
 *  the path a value a HOST supplies, and a quote plus a closing tag then
 *  injects markup into the rendered SVG. The close mark, the search trigger
 *  and every inert glyph already escape for exactly this reason. Escaping a
 *  literal path costs nothing: a path is digits, letters, spaces, commas and
 *  minus signs, none of which `esc` touches. */
const glyph = (d: string, label: string): string =>
  `<svg class="m-ws-glyph" viewBox="0 0 24 24" width="18" height="18" fill="none" `
  + `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" `
  + `role="img" aria-label="${esc(label)}"><path d="${esc(d)}"/></svg>`;

/**
 * The activity ribbon: a narrow rail of tool icons with the way out at the
 * foot. EVERY button on it does something, or it is not on it — @Codex, P1
 * on b0af641: the first version drew Search and Graph as inert pictures,
 * and 0055 forbids a visual imitation with dead controls. Each carries a
 * `data-ws-action` the HOST routes (`files` moves focus into the explorer,
 * `new` creates a note through the host's own create action); `data-ws-exit`
 * is the way back to the host. Search and Graph return when they have
 * something to do.
 */
const workspaceRibbon: CapabilityRenderer = (_node, input) => {
  const r = input?.capability === 'workspace-ribbon' ? input.ribbon : undefined;
  const selects = r?.selects ?? {};
  /* OWN PROPERTIES ONLY — @Codex's P2 on `d881f5b`. Both the inventory and the
     mapping are the HOST's now, so a look declaring a tool called `toString`,
     `constructor` or `valueOf` reads `Object.prototype` through this lookup:
     the value is not `undefined`, so the tool draws as `role="tab"` inside a
     tablist, `aria-selected` compares a function to a purpose and is always
     false, and the tab selects nothing. It is the shape `0193` already
     records — a control that EXISTS is not a control that works — reached
     through a name rather than a placement.

     ONE function rather than three guards, because THREE reads have to agree:
     the tab markup, the contiguous-run partition, and the roving tabindex.
     Two of them saying tab and one saying command is a worse product than
     either, and that is what a list of guards drifts into. */
  const paneFor = (action: string): RegionPurpose | undefined =>
    (Object.prototype.hasOwnProperty.call(selects, action) ? selects[action] : undefined);
  /**
   * THE STRIP'S TOOLS, in one ordered table — @Codex's P2 on `3b84c58`.
   *
   * The tablist membership was a hard-coded `['files','search']` while
   * `tool()` gave `role="tab"` to ANY action the look mapped. A look mapping
   * `graph` therefore got a tab sitting OUTSIDE the tablist — an orphan,
   * which is a worse accessibility model than the one this replaced — and
   * there was no table to add a sixth tool to at all.
   *
   * One table now, partitioned by the DECLARATION: everything the look maps
   * is a tab and goes inside a list, everything else is a command beside it.
   *
   * A tablist must be CONTIGUOUS, which is what makes it a tablist rather than
   * a box with tabs scattered through it. That used to be achieved by drawing
   * every selector first — which silently re-ordered a declared inventory, and
   * is @Codex's P2 on `e02bf93`. The inventory is cut into contiguous RUNS
   * below instead: each run of selectors is its own tablist, each command is
   * emitted where it stands, and the declared order is what renders.
   */
  const TOOLS: readonly RibbonTool[] = r?.tools ?? [
    { action: 'files', label: 'Files', glyph: 'M3 7h6l2 2h10v10H3z' },
    // Search RETURNS with a surface behind it (the eighth verdict's list):
    // the host switches the left sidebar to the search pane and puts the
    // caret in its query.
    { action: 'search', label: 'Search', glyph: 'M11 4a7 7 0 1 0 0 14a7 7 0 0 0 0-14zM20 20l-4-4' },
    { action: 'new', label: 'New note', glyph: 'M12 5v14M5 12h14' },
    // Graph RETURNS with a surface behind it (the tenth verdict's list): the
    // host opens the vault's Graph view in the main column.
    { action: 'graph', label: 'Graph view',
      glyph: 'M6 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM18 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM18 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM7.7 14.2l8.6-7.4M7.7 15.8l8.6 3.4' },
  ];
  /** A tool that CHOOSES a pane draws as a tab; one that acts draws as a
   *  button. The distinction is the host's declaration, never a name this
   *  file knows. */
  const tool = (t: RibbonTool, tabbable = true): string => {
    const pane = paneFor(t.action);
    const on = pane !== undefined && pane === r?.active;
    const tab = pane !== undefined
      ? ` role="tab" aria-selected="${on}"${tabbable ? '' : ' tabindex="-1"'}` : '';
    return `<button type="button" class="m-ws-tool${on ? ' m-ws-tool-on' : ''}"`
      + `${tab} data-ws-action="${esc(t.action)}" aria-label="${esc(t.label)}">${glyph(t.glyph, t.label)}</button>`;
  };
  /* THE DECLARED ORDER SURVIVES — @Codex's P2 on `e02bf93`. `tools` promises
     an inventory "in order", and partitioning it into selectors and commands
     and painting every selector first quietly re-arranges it: a look declaring
     Files (tab), New (command), Search (tab) was painted Files, Search, New.
     A composed or seventh look could not express its own arrangement, which is
     `0173`'s standing question answered the wrong way by a filter.

     So the inventory is walked ONCE and cut into CONTIGUOUS RUNS. A run of
     selectors becomes a tablist; a command is emitted where it stands. The
     tablist still holds only tabs — the accessibility rule that motivated the
     partition is untouched, because it was never the ordering that enforced
     it. Every look shipped today declares its selectors contiguously, so all
     six render exactly the byte sequence they did. */
  const runs: { tabs: boolean; items: RibbonTool[] }[] = [];
  for (const t of TOOLS) {
    const tabs = paneFor(t.action) !== undefined;
    const last = runs[runs.length - 1];
    if (last && last.tabs === tabs) last.items.push(t);
    else runs.push({ tabs, items: [t] });
  }
  /* AN ACTION NOBODY DECLARED A TOOL FOR IS REFUSED, NOT DISCARDED —
     @Codex's P2 on `d80adab`, and the comment that stood here said the
     opposite in as many words: *"drawn by nobody"*. A look mapping a pane to
     an action outside the inventory got a `selects` entry the renderer
     accepted and then filtered away, so the pane it named was unreachable and
     nothing anywhere said so. It is a refusal now, in the vocabulary every
     look harness already counts, so the silence is the one thing it cannot
     be. */
  const unknown = Object.keys(selects).filter((a) => !TOOLS.some((t) => t.action === a)).sort();
  const refusal = unknown.length
    ? `<div class="m-capability m-capability-refused" role="note">`
      + `No ribbon tool is declared for ${esc(unknown.join(', '))}, so the pane it selects `
      + `cannot be reached. Declare the tool on the ribbon, or drop the mapping.</div>`
    : '';
  // THE TABLIST HOLDS ONLY TABS. Commands sit beside it — the same rule
  // `workspaceTabs` follows for its `+`, and for the same reason: a command
  // inside a tablist gives assistive technology an invalid model of the
  // widget.
  /* ROVING TABINDEX IS PER TABLIST, and the runs above made that a set of
     more than one — @Codex's P2 on `98376fb`, and a consumer that assumed a
     closed set inside my own repair of their previous finding. A roving
     tabindex leaves exactly ONE tab of a widget in the sequential order;
     `tabindex="-1"` on every inactive selector is right for one tablist and
     strands the others, because Tab cannot reach them and the arrow handler
     returns on a run of fewer than two tabs. An interleaved inventory (Files
     tab, New command, Search tab) put Search behind no key at all.

     The active tab where this run holds it, else the run's FIRST — which is
     what the APG asks of a tablist whose selection lives elsewhere, and what
     the shipped looks already render: all six declare their selectors
     contiguously, so the one run holding the active one draws the bytes it
     did. */
  const body = runs.map((run) => {
    if (!run.tabs) return run.items.map((t) => tool(t)).join('');
    const at = Math.max(0, run.items.findIndex((t) => paneFor(t.action) === r?.active));
    return `<div class="m-ws-tool-tabs" role="tablist" aria-label="Sidebar panes">`
      + run.items.map((t, i) => tool(t, i === at)).join('') + `</div>`;
  }).join('');
  return `<nav class="m-capability m-ws-ribbon" aria-label="Ribbon">`
    + refusal
    + body
    + `<span class="m-ws-ribbon-space"></span>`
    + `<button type="button" class="m-ws-tool m-ws-exit" data-ws-exit aria-label="Back to Morph">`
    + `${glyph('M3 11l9-8 9 8v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z', 'Back to Morph')}</button>`
    + `</nav>`;
};

/** The tab strip: one tab per open object, the active one lifted. Empty
 *  input draws an empty strip, not a tab called "undefined". */
const workspaceTabs: CapabilityRenderer = (_node, input) => {
  const tabs = input?.capability === 'workspace-tabs' ? input.tabs.tabs : [];
  const strip = input?.capability === 'workspace-tabs' ? input.tabs : undefined;
  // The × on the open tab and the + after the strip. The + is a real New
  // note for a writer, and absent otherwise; the × is the look's glyph,
  // drawn inert (0056) or as a real control depending on whether the host
  // says it can close. Neither is a constant.
  const closeMark = strip?.inert?.close;
  const closeFor = (handle: ObjectHandle | undefined, label: string): string => {
    if (!closeMark) return '';
    /**
     * NOTHING, when the host says this strip cannot close NOW — @Codex's P2
     * on `4a34aef`.
     *
     * `close` has three states and they are not two. Undefined is a host with
     * no closing at all, and the look's glyph is then declared chrome under
     * `0056`, correct as imagery. FALSE is a host that closes and refuses for
     * this strip — the last tab, which `0198` protects so the editor can
     * never be closed into nothing — and drawing the mark there is a control's
     * appearance without its function, which is the thing `0056` is not for.
     *
     * Measured before the fix: one tab, one `span.m-inert.m-ws-tab-x`,
     * `aria-hidden`, 11x11, visible. I had told @Codex in writing that the
     * last tab has no ×; it had one, and only the behaviour was right.
     */
    if (strip?.close === false) return '';
    /**
     * AND NOTHING FOR A TAB WITH NO CLOSE TARGET — @Codex's P2 on `e5ffb92`,
     * which is the SAME defect as the paragraph above, one case over.
     *
     * `close` is a fact about the STRIP and `handle` is a fact about this TAB,
     * and the two were being read as one. Obsidian's Graph view is a tab with
     * no handle — it is a place, not an open object — so with the note beside
     * it the strip says `close: true` and this drew the look's inert × on the
     * Graph tab, where the host has nothing to close. A control's appearance
     * without its function, again.
     *
     * The undefined-`close` case below still draws it, and that is not the
     * same thing: there the look's glyph is declared chrome under `0056`
     * because the host does no closing at all, so nothing is being imitated
     * falsely. Here the host DOES close, and this tab is the exception.
     */
    if (strip?.close && handle === undefined) return '';
    if (!strip?.close) return inertItem(closeMark, 'm-ws-tab-x');
    // A REAL control: the look's glyph, the tab's own handle, and a name.
    // `aria-hidden` comes off with the inert class — a button nobody can
    // hear is the dead control this repo forbids in an imitation.
    const glyph = closeMark.glyph
      ? `<svg class="m-inert-glyph" viewBox="0 0 24 24" width="14" height="14" fill="none" `
        + `stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">`
        + `<path d="${esc(closeMark.glyph)}"/></svg>`
      : esc(closeMark.label);
    return `<button type="button" class="m-ws-tab-x m-ws-tab-close" `
      + `data-ws-tab-close="${esc(handle)}" aria-label="Close ${esc(label)}">${glyph}</button>`;
  };
  /* THE + IS ORDERED PAST EVERY TAB, and it has to be said rather than left to
     DOM order once the tabs carry an `order` of their own: an item with the
     default `order:0` joins the group holding tab 0, and DOM order then puts
     the + third in the strip rather than last. Measured, not reasoned about —
     the capture put it at x453 between the first tab's close and the second
     tab. `tabs.length` is the smallest value that clears every tab index. */
  const plus = strip?.create
    ? `<button type="button" class="m-ws-tab-plus" data-ws-action="new" aria-label="New note" `
      + `style="order:${tabs.length}">+</button>`
    : '';
  /**
   * A TABLIST OWNS ONLY TABS — decision `0072`, and it replaces the
   * presentational slot this function used to wrap each chip in.
   *
   * `0198` put the close button INSIDE the `role="tab"` element: two focusable
   * controls nested one in the other, and a tab's descendants may be flattened
   * to presentational content, so the separately named Close was exactly the
   * control that could vanish. The repair for that was a
   * `role="presentation"` slot holding the tab and the close side by side, and
   * its reasoning was half right: presentation DOES keep the tablist's
   * ownership of its tabs. What it does not do is un-role the wrapper's
   * DESCENDANTS. The button stayed an owned child of the tablist, so with
   * three notes open assistive technology received a tablist owning three tabs
   * and one ordinary button — the same invalid model @Codex's P2 on `8957373`
   * removed for the `+`, arriving one element further in.
   *
   * `0072` chose the repair that keeps both the tab semantics and the visible
   * affordance: the close is a SIBLING of the tablist, in an action slot that
   * names and acts on the active tab. Both cheaper answers are refused there
   * by name — removing the button "would improve structural validity by
   * deleting a real capability", and a button group "would make
   * assistive-technology users infer selection from styling".
   *
   * ORDER, because the slot's placement is not free. Obsidian sets
   * `display:contents` on the tablist, so the TABS are flex items of the
   * frame — and a sibling placed after the tablist therefore lands after the
   * LAST tab, not after the ACTIVE one. Those coincide on a specimen whose
   * last note is the open one and diverge on the next gesture, which is the
   * one-tab false-clean trap in a new place. Giving each tab its index as
   * `order` and the action slot the ACTIVE index puts the slot in the same
   * order group as the tab it belongs to, where DOM order settles it — the
   * tablist comes first, so the slot lands immediately after its own tab
   * wherever that tab is. A look whose tablist keeps its box is unaffected:
   * there the slot is an adjacent layout slot, which `0072` also permits.
   */
  const activeAt = tabs.findIndex((t) => t.active);
  const items = tabs.map((t, i) => {
    /* ROVING TABINDEX — `0072`'s keyboard contract asks for exactly one tab in
       the sequential order, so that Tab from the selected tab reaches Close
       next rather than walking the rest of the strip first. Safe to take the
       others out only because the generic `[role="tablist"]` arrow handler
       exists: `0194` gave a tab `tabindex="-1"` WITHOUT one and made a pane
       unreachable by keyboard, which is the half of the roving model that
       takes reachability away. */
    const ord = ` style="order:${i}"`;
    // The ACTIVE tab is a heading for what is open; an inactive one is a
    // BUTTON that reopens its object, named by the handle the host gave it.
    // The active tab is FOCUSABLE, so a keyboard user who activated a tab
    // has somewhere to land after the repaint (@Codex, P2 on b0af641).
    if (t.active) {
      return `<div class="m-ws-tab m-ws-tab-on" role="tab" aria-selected="true" tabindex="0"${ord}>`
        + `${esc(t.label)}</div>`;
    }
    if (t.handle === undefined) {
      return `<div class="m-ws-tab" role="tab" aria-selected="false" tabindex="-1"${ord}>`
        + `${esc(t.label)}</div>`;
    }
    return `<button type="button" class="m-ws-tab" role="tab" aria-selected="false" tabindex="-1" `
      + `data-ws-tab="${esc(t.handle)}"${ord}>${esc(t.label)}</button>`;
  }).join('');
  /* The slot is omitted ENTIRELY when the host supplies no close for the
     active tab — `0072` term 9, and the last surviving tab is the case:
     `0198` refuses to close the editor into nothing, and an empty box left
     behind would reserve the chip's trailing room for a control that is not
     there. */
  const closeMarkup = activeAt >= 0
    ? closeFor(tabs[activeAt]?.handle, tabs[activeAt]?.label ?? '') : '';
  const action = closeMarkup
    ? `<div class="m-ws-tab-action" style="order:${activeAt}">${closeMarkup}</div>` : '';
  // The tablist owns the tabs alone: the + is a command, not a tab, so it
  // sits beside the list, not inside it (@Codex, P2 on 8957373 — a plain
  // action button as a tablist's child gives assistive technology an
  // invalid model of the widget). The action slot is beside it for the same
  // reason, one element further in.
  return `<div class="m-capability m-ws-tabs"><div class="m-ws-tablist" role="tablist" aria-label="Open notes">${items}</div>${action}${plus}</div>`;
};

/** The status bar: the vault, then what the open note counts to. Every
 *  number is said only when the host supplied it. */
const workspaceStatus: CapabilityRenderer = (_node, input) => {
  const s = input?.capability === 'workspace-status' ? input.status : undefined;
  const parts: string[] = [];
  // `m-ws-status-vault`, not `m-ws-vault`: the explorer's vault strip owns
  // that name, and sharing it put the strip's block layout on this span —
  // seen as "● Notes" wrapping under the status bar.
  if (s?.vault !== undefined) parts.push(`<span class="m-ws-status-vault">● ${esc(s.vault)}</span>`);
  if (s?.backlinks !== undefined)
    parts.push(`<span>${s.backlinks} ${s.backlinks === 1 ? 'backlink' : 'backlinks'}</span>`);
  if (s?.words !== undefined) parts.push(`<span>${s.words.toLocaleString('en-US')} words</span>`);
  if (s?.characters !== undefined)
    parts.push(`<span>${s.characters.toLocaleString('en-US')} characters</span>`);
  if (s?.format !== undefined) parts.push(`<span>${esc(s.format)}</span>`);
  // The app's own chrome, inert (0056): at the start before the counts,
  // at the end after them.
  const inert = s?.inert ?? [];
  const start = inert.filter((i) => i.place !== 'end').map((i) => inertItem(i)).join('');
  const end = inert.filter((i) => i.place === 'end').map((i) => inertItem(i)).join('');
  return `<div class="m-capability m-ws-status" role="status" aria-label="Status bar">`
    + `${start}${parts.join('')}${end ? `<span class="m-ws-status-end" aria-hidden="true">${end}</span>` : ''}</div>`;
};

renderers.set('workspace-ribbon@0.1.0', workspaceRibbon);

/** The window's controls: one inert row, drawn as imagery and nothing else.
 *  Empty input draws an empty row rather than a row of undefined. */
const workspaceWindow: CapabilityRenderer = (_node, input) => {
  const controls = input?.capability === 'workspace-window' ? input.window.controls : [];
  // THE SAME `m-winctl` ROW every carrier uses — Notion's sidebar draws one
  // too. One rule for the group's spacing and alignment, so a look that
  // declares controls gets the reference's 12px circles on a 20px pitch
  // wherever its shell puts them, rather than a second rule that drifts.
  return `<div class="m-capability m-winctl m-ws-winrow" aria-hidden="true">`
    + controls.map((c) => inertItem(c)).join('') + `</div>`;
};
renderers.set('workspace-window@0.1.0', workspaceWindow);
renderers.set('workspace-tabs@0.1.0', workspaceTabs);
renderers.set('workspace-status@0.1.0', workspaceStatus);

/**
 * The SEARCH tool: a query box and a count — Obsidian's search pane head.
 * A registered module rather than a renderer branch on the pane's purpose
 * (@Codex, P1 on 93d57d7: 0055 wants app components registered with
 * contracts, and a module the host lacks is then a refusal in words, not
 * a pane missing its box). The hits are an ordinary collection the host
 * answers beside it. The query is echoed so a repaint keeps it; a pressed
 * Search puts the caret here (the host's job, by `data-ws-search`), and
 * typing is what answers it.
 */
const workspaceSearch: CapabilityRenderer = (_node, input) => {
  const s = input?.capability === 'workspace-search' ? input.search : undefined;
  const q = s?.query ?? '';
  const count = s?.count;
  // The module's root lays out as nothing (`display:contents`), so the
  // padded box and the count are the pane's own children — the geometry
  // the renderer branch had, byte for byte in the twelfth verdict's
  // pixels. The thirteenth caught the count inside the padded box, a
  // few pixels up: a module that replaces a branch reproduces its
  // geometry, not just its markup.
  // The header and the composer's chrome, inert (0056): `start` items in
  // the header beside the heading, the rest inside the composer's box.
  const inert = s?.inert ?? [];
  const head = s?.heading || inert.some((i) => i.place === 'start')
    ? `<div class="m-ws-search-head">${s?.heading ? `<span class="m-ws-search-heading">${esc(s.heading)}</span>` : ''}`
      + inert.filter((i) => i.place === 'start').map((i) => inertItem(i)).join('') + `</div>`
    : '';
  const inBox = inert.filter((i) => i.place !== 'start').map((i) => inertItem(i)).join('');
  // COLLAPSED, when the look asks for it: the trigger stands in the row and
  // the box is not in the document's tab order until it is open. `hidden`
  // rather than a class, so the box leaves the accessibility tree with the
  // same attribute that takes it out of the flow.
  const trigger = s?.trigger;
  const open = trigger === undefined || s?.open === true;
  const triggerMarkup = trigger === undefined ? '' :
    `<button type="button" class="m-ws-search-open" data-ws-search-open data-ws-action="search"`
    + ` aria-label="${esc(trigger.label)}" aria-expanded="${open}">`
    + `<svg class="m-ws-search-glyph" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"`
    + ` stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${esc(trigger.glyph)}"/></svg>`
    + `</button>`;
  return `<div class="m-capability m-ws-search-tool">${head}${triggerMarkup}`
    + `<div class="m-ws-search-box"${open ? '' : ' hidden'}><input class="m-ws-search-input" type="search" data-ws-search placeholder="${esc(s?.placeholder ?? 'Search…')}"`
    + ` aria-label="${esc(s?.label ?? 'Search the vault')}" value="${esc(q)}" autocomplete="off">`
    + (inBox ? `<div class="m-ws-search-inert" aria-hidden="true">${inBox}</div>` : '') + `</div>`
    + (q && count !== undefined
      ? `<div class="m-ws-section">${count === 0 ? 'No matches' : `${count} ${count === 1 ? 'match' : 'matches'}`}</div>`
      : '')
    + `</div>`;
};
renderers.set('workspace-search@0.1.0', workspaceSearch);

/** One inert item (0056): a span, aria-hidden, no role, no name, its glyph
 *  a text glyph or an SVG path. `extra` adds the caller's classes. */
export const inertItem = (it: InertItem, extra = ''): string => {
  // A dot REPLACES the glyph rather than joining it: a window control is a
  // circle and nothing else, and an item declaring both is asking for two
  // presentations of one item.
  const glyph = it.dot !== undefined
    ? `<span class="m-inert-dot" style="background:${esc(it.dot)}"></span>`
    : it.glyph === undefined ? ''
    : it.glyph.startsWith('M')
      ? `<svg class="m-inert-glyph" viewBox="0 0 24 24" width="${it.big ? 24 : 16}" height="${it.big ? 24 : 16}" fill="none" stroke="currentColor" `
        + `stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="${esc(it.glyph)}"/></svg>`
      : `<span class="m-inert-glyph">${esc(it.glyph)}</span>`;
  const cls = `m-inert${it.big ? ' m-inert-big' : ''}${it.box ? ' m-inert-box' : ''}${it.place ? ` m-inert-${it.place}` : ''}${it.bar ? ' m-inert-swatch' : ''}${it.sep ? ' m-wd-sep' : ''}${extra ? ` ${extra}` : ''}`;
  const bar = it.bar ? `<span class="m-inert-bar" style="background:${esc(it.bar)}"></span>` : '';
  const brk = it.break ? '<span class="m-inert-break" aria-hidden="true"></span>' : '';
  const menu = it.menu ? `<span class="m-inert-menu">▾</span>` : '';
  return `${brk}<span class="${cls}" aria-hidden="true">${glyph}${bar}${it.label ? `<span class="m-inert-label">${esc(it.label)}</span>` : ''}${menu}</span>`;
};

/* ───────────────────────────── Notion's shell — decision 0055's second look */

/** `d` escaped here too, though every caller today passes a literal from this
 *  file: the sibling helper's path became host-supplied the moment a look
 *  could declare a ribbon tool, and the next input-carried glyph will not
 *  announce which helper it reaches. Escaping closes the class rather than
 *  the instance, and costs nothing on a path. */
const ntGlyph = (d: string, label: string, size = 16): string =>
  `<svg class="m-glyph" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" `
  + `stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" role="img" aria-label="${esc(label)}"><path d="${esc(d)}"/></svg>`;
const NT_HOME = 'M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10';
const NT_GLOBE = 'M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18zM3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18';
const NT_PAGE = 'M6 3h9l5 5v13H6zM14 3v6h6';
const NT_TABLE = 'M3 5h18v14H3zM3 10h18M3 15h18M9 5v14';
const NT_BACK = 'M15 5l-7 7 7 7';
const NT_FORWARD = 'M9 5l7 7-7 7';
const NT_STAR = 'M12 3l2.7 5.9 6.3.7-4.7 4.3 1.3 6.3L12 17l-5.6 3.2 1.3-6.3L3 9.6l6.3-.7z';
const NT_SEARCH = 'M10.5 4a6.5 6.5 0 1 0 0 13a6.5 6.5 0 0 0 0-13zM15.5 15.5L20 20';
const NT_COMPOSE = 'M4 20h4l10-10-4-4L4 16zM13 7l4 4';
/** The sidebar toggle: a window with its left pane. */
const NT_SIDEBAR = 'M3 5h18v14H3zM9 5v14';
/** A row's twisty: the disclosure triangle, pointing down when open. */
const NT_TWISTY = 'M6 9l6 6 6-6';

/** The nav sidebar: the workspace switcher, Home, then the sections the host
 *  resolved — each row a button the host routes by `data-morph-go`. */
const workspaceNav: CapabilityRenderer = (_node, input) => {
  const n = input?.capability === 'workspace-nav' ? input.nav : undefined;
  /* Sanitised to the class-name grammar rather than trusted: this string
     reaches an attribute, and a host that passed a quote would be writing
     markup through a field that says it names classes. */
  if (!n) return `<nav class="m-capability m-nav" aria-label="Sidebar"></nav>`;
  // A section without a label (Coda's page tree names no sections) is a
  // bare list: no empty label div to leave a gap.
  const sections = n.sections.map((sec) =>
    `<div class="m-nav-section">${sec.label ? `<div class="m-nav-section-label">${esc(sec.label)}</div>` : ''}`
    + sec.rows.map((r) => {
      // A row with pages inside gets a TWISTY before its icon and its pages
      // after it, both keyed on the row's fold: the host paints
      // `data-morph-collapsed` from view state (decision 0014), so which
      // rows are open is per person and per device and reaches no genome.
      // The twisty is its own button — pressing it discloses, pressing the
      // row still opens the pool, which is the app's own division.
      const kids = r.children ?? [];
      const key = kids.length && r.fold ? r.fold : undefined;
      const twisty = key
        // The toggle carries its own NOUN: the host rebuilds the label on
        // every fold, and a tree row's container has no `aria-label` to
        // rebuild it from — the twisty announced "Expand" with nothing after
        // it (seen). Named here, the host prefers this over the container's.
        ? `<button type="button" class="m-nav-twisty" data-morph-collapse="${esc(key)}"`
          + ` data-morph-collapse-name="${esc(r.label)}" aria-expanded="true"`
          + ` aria-label="Collapse ${esc(r.label)}">${ntGlyph(NT_TWISTY, '', 14)}</button>`
        : '';
      const row = `<button type="button" class="m-nav-row${r.current ? ' m-nav-row-on' : ''}" data-morph-go="${esc(r.action)}"`
        // TASK 0178: the row carries its own tone, so a look's palette is
        // keyed by WHICH ITEM this is rather than by where it sits.
        + ` data-m-tone="${toneOf(r.action, r.tone)}"`
        + `${r.current ? ' aria-current="page"' : ''}>`
        + `<span class="m-nav-row-icon" aria-hidden="true">${r.icon ? esc(r.icon) : ntGlyph(NT_PAGE, '', 16)}</span>`
        + `<span class="m-nav-row-label">${esc(r.label)}</span>`
        + (r.detail ? `<span class="m-nav-row-detail">${esc(r.detail)}</span>` : '')
        + `</button>`;
      if (!key) return row;
      return `<div class="m-nav-tree" data-morph-collapse-key="${esc(key)}">`
        + `<div class="m-nav-rowline">${twisty}${row}</div>`
        + `<div class="m-nav-kids">`
        + kids.map((k) =>
          `<button type="button" class="m-nav-row m-nav-kid${k.current ? ' m-nav-row-on' : ''}" data-morph-go="${esc(k.action)}"`
          + `${k.current ? ' aria-current="page"' : ''}>`
          + `<span class="m-nav-row-icon" aria-hidden="true">${k.icon ? esc(k.icon) : ntGlyph(NT_PAGE, '', 16)}</span>`
          + `<span class="m-nav-row-label">${esc(k.label)}</span></button>`).join('')
        + `</div></div>`;
    }).join('')
    + `</div>`).join('');
  // The fold: the key on the nav (the host paints `data-morph-collapsed`
  // from view state) and a toggle at its head, beside the workspace.
  const foldKey = n.fold ? ` data-morph-collapse-key="${esc(n.fold)}"` : '';
  const foldToggle = n.fold
    ? `<button type="button" class="m-fold-toggle" data-morph-collapse="${esc(n.fold)}" aria-expanded="true" aria-label="Collapse Sidebar">${ntGlyph(NT_SIDEBAR, '', 18)}</button>`
    : '';
  // The window controls sit above everything the sidebar holds, which is
  // where a macOS window puts them — outside the switcher, not inside it.
  const win = n.window?.length
    ? `<div class="m-winctl" aria-hidden="true">`
      + n.window.map((i) => inertItem(i)).join('') + `</div>`
    : '';
  return `<nav class="m-capability m-nav" aria-label="Sidebar"${foldKey}>`
    + win
    + `<div class="m-nav-workspace">${ntGlyph(NT_GLOBE, '', 18)}<span class="m-nav-workspace-name">${esc(n.workspace)}</span>`
    + `<span class="m-chev" aria-hidden="true">⌄</span>${foldToggle}</div>`
    + `<div class="m-tools"><button type="button" class="m-tool m-tool-on" data-morph-go="home" aria-label="Home">`
    + `${ntGlyph(NT_HOME, '', 18)}<span class="m-tool-label">Home</span></button>`
    + (n.inert ?? []).map((i) => inertItem(i, 'm-tool m-tool-inert')).join('')
    + (n.search ? `<button type="button" class="m-tool m-tool-icon" data-ws-action="search" aria-label="Search">${ntGlyph(NT_SEARCH, '', 18)}</button>` : '')
    + `</div>`
    + sections
    // The sidebar's foot: the reference's compose button, which makes a page.
    + (n.create ? `<div class="m-nav-bottom"><button type="button" class="m-nav-newpage" data-ws-action="new">`
      + `${ntGlyph(NT_COMPOSE, '', 16)}<span>New page</span></button></div>` : '')
     + `</nav>`;
};
renderers.set('workspace-nav@0.1.0', workspaceNav);

/** The topbar: the strip — back and forward over the places visited under
 *  the look (only when the host backs them), the page's tab (icon and name,
 *  the open one) and a + that creates a page — and, right-aligned under it
 *  where the reference's Share, comment, star and ··· sit, the two actions
 *  the host has: the star (the page on the hub's shelf; lit, off again)
 *  and ··· opening New page and the way out of the look. The host routes
 *  `data-morph-go` (back, forward, star, more, leave) and `data-ws-action`
 *  (new). Share and comment are not drawn: the host has no action for
 *  them, and a dead control is a lie about the product (0055). */
const workspaceTopbar: CapabilityRenderer = (_node, input) => {
  const t = input?.capability === 'workspace-topbar' ? input.topbar : undefined;
  const title = t?.title ?? '';
  const a = t?.actions;
  const chevrons = a
    ? `<button type="button" class="m-chev" data-morph-go="back" aria-label="Back"${a.canGoBack ? '' : ' disabled'}>${ntGlyph(NT_BACK, '', 18)}</button>`
      + `<button type="button" class="m-chev" data-morph-go="forward" aria-label="Forward"${a.canGoForward ? '' : ' disabled'}>${ntGlyph(NT_FORWARD, '', 18)}</button>`
    : '';
  // The bar's inert chrome (0056), by place: Word's AutoSave and undo at
  // the start, the search at the centre, Sign in and the window controls
  // at the end. Imagery beside the strip, never a control.
  const inert = t?.inert ?? [];
  const at = (place: 'start' | 'centre' | 'end') => inert.filter((i) => (i.place ?? 'start') === place).map((i) => inertItem(i)).join('');
  // The tablist owns the tab alone; the chevrons and + are commands beside
  // it, not tabs (@Codex, P2 on 8957373: an action button as a tablist's
  // child gives assistive technology an invalid model of the widget).
  // With the sidebar folded the bar's start carries the expand toggle and
  // the workspace's identity: the same key, so the host paints both from
  // one entry, and the stylesheet shows this block only when folded.
  const compact = t?.fold
    ? `<div class="m-fold-compact" data-morph-collapse-key="${esc(t.fold.key)}" aria-label="Sidebar">`
      + `<button type="button" class="m-fold-toggle" data-morph-collapse="${esc(t.fold.key)}" aria-expanded="true" aria-label="Collapse Sidebar">${ntGlyph(NT_SIDEBAR, '', 18)}</button>`
      + `${ntGlyph(NT_GLOBE, '', 18)}<span class="m-fold-compact-name">${esc(t.fold.workspace)}</span></div>`
    : '';
  const tabs = t?.strip === false ? '' : chevrons
    + `<div class="m-topbar-tablist" role="tablist" aria-label="Page tabs">`
    + `<div class="m-topbar-tab m-topbar-tab-on" role="tab" aria-selected="true">`
    + `<span class="m-topbar-tab-icon" aria-hidden="true">${t?.icon ? esc(t.icon) : ntGlyph(NT_PAGE, '', 16)}</span>`
    + `<span class="m-topbar-tab-name">${esc(title)}</span></div></div>`
    + `<button type="button" class="m-topbar-tab-add" data-ws-action="new" aria-label="New page">+</button>`;
  const strip = `<div class="m-topbar-strip">${compact}${tabs}`
    + at('start')
    + `</div>`
    + (inert.some((i) => i.place === 'centre') ? `<div class="m-topbar-centre" aria-hidden="true">${at('centre')}</div>` : '')
    + (inert.some((i) => i.place === 'end') ? `<div class="m-topbar-end" aria-hidden="true">${at('end')}</div>` : '');
  const actions = a
    ? `<div class="m-topbar-actions">`
      + inert.filter((i) => i.place === 'actions').map((i) => inertItem(i, 'm-topbar-action-inert')).join('')
      + (a.kept === undefined ? '' : `<button type="button" class="m-topbar-star" data-morph-go="star" aria-pressed="${a.kept ? 'true' : 'false'}" `
        + `aria-label="${a.kept ? 'Take off the hub' : 'Keep on the hub'}">`
        + `<svg class="m-glyph" viewBox="0 0 24 24" width="20" height="20" fill="${a.kept ? 'currentColor' : 'none'}" `
        + `stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="${NT_STAR}"/></svg></button>`)
      + `<button type="button" class="m-topbar-more" data-morph-go="more" aria-haspopup="menu" aria-expanded="false" aria-label="More">···</button>`
      + `<div class="m-topbar-menu" role="menu" hidden>`
      + `<button type="button" role="menuitem" class="m-topbar-menu-item" data-ws-action="new">New page</button>`
      + `<button type="button" role="menuitem" class="m-topbar-menu-item" data-morph-go="leave">${esc(a.leave)}</button>`
      + `</div></div>`
    : '';
  return `<div class="m-capability m-topbar" role="navigation" aria-label="Page">${strip}${actions}</div>`;
};
renderers.set('workspace-topbar@0.1.0', workspaceTopbar);

/** The page head: a round icon, then the page's name, large and bold. */
const workspacePagehead: CapabilityRenderer = (_node, input) => {
  const h = input?.capability === 'workspace-pagehead' ? input.pagehead : undefined;
  // What a DOC's page owns, each drawn only when the host gives it: the
  // cover band (a CSS image, inline on the band's own variable so the
  // stylesheet decides its size), the breadcrumb, the paragraph.
  const cover = h?.cover ? `<div class="m-cd-cover" style="--cd-cover:${esc(h.cover)}" aria-hidden="true"></div>` : '';
  const crumb = h?.crumb ? `<div class="m-cd-crumb">${esc(h.crumb)}</div>` : '';
  const words = h?.words ? `<p class="m-cd-words">${esc(h.words)}</p>` : '';
  // NO TITLE, NO TITLE ROW. A head asked to draw nothing used to emit an empty
  // `h1` and a default page glyph — a heading with no text is a heading a
  // screen reader still announces, and the glyph is the POOL's on a page about
  // one object. Task 0176: the object's own editable title is the page title,
  // so the head steps out of its way rather than duplicating it.
  const titled = (h?.title ?? '') !== '';
  const row = titled
    ? `<div class="m-pagehead-titlerow">`
      + `<span class="m-pagehead-icon" aria-hidden="true">${h?.icon ? esc(h.icon) : ntGlyph(NT_PAGE, '', 44)}</span>`
      + `<h1 class="m-pagehead-title">${esc(h?.title ?? '')}</h1></div>`
    : '';
  return `<header class="m-capability m-pagehead">${cover}${crumb}${row}${words}</header>`;
};
renderers.set('workspace-pagehead@0.1.0', workspacePagehead);

/** Buttons as BLOCKS — Coda's button row in a doc. One button per action
 *  the host backs, routed by `data-ws-action`; none fed, none drawn. */
const docActions: CapabilityRenderer = (_node, input) => {
  const a = input?.capability === 'doc-actions' ? input.actions : undefined;
  const buttons = (a?.actions ?? []).map((b) =>
    `<button type="button" class="m-cd-button" data-ws-action="${esc(b.action)}">${esc(b.label)}</button>`).join('');
  // The heading row: the view's name at the left, the table's inert
  // tools at the right, joined to the grid beneath as one working block
  // (the twentieth verdict's first Coda item).
  const tools = a?.inert?.length ? `<span class="m-cd-tools" aria-hidden="true">${a.inert.map((i) => inertItem(i, 'm-cd-tool')).join('')}</span>` : '';
  return `<div class="m-capability m-cd-actions">${buttons}</div>`
    + (a?.heading ? `<div class="m-cd-headrow"><h3 class="m-cd-heading">${esc(a.heading)}</h3>${tools}</div>` : '');
};
renderers.set('doc-actions@0.1.0', docActions);

/** The ribbon: the tab row (the one tab the host names, active) over the
 *  band — groups of controls with the group's label beneath, as Word
 *  draws them. A style tile carries its style as a class so the sheet
 *  previews it (Normal in body type, Heading in heading type). */
const docRibbon: CapabilityRenderer = (_node, input) => {
  const r = input?.capability === 'doc-ribbon' ? input.ribbon : undefined;
  // An item with neither an action nor `inert: true` is a contradiction
  // the input's type forbids; at runtime it is refused in words rather
  // than drawn as a control that does nothing.
  const bad = (r?.groups ?? []).flatMap((g) => g.items).find((it) => !('action' in it && it.action) && !('inert' in it && it.inert));
  if (bad) return `<div class="m-capability m-capability-refused">the ribbon item "${esc(bad.label)}" has no action and is not declared inert</div>`;
  const groups = (r?.groups ?? []).filter((g) => g.items.length > 0).map((g) =>
    `<div class="m-wd-group"><div class="m-wd-items">`
    + g.items.map((it) => {
      if ('inert' in it && it.inert) return inertItem(it, `m-wd-item${it.tile ? ' m-wd-tile' : ''}`);
      const glyph = it.glyph ? `<span class="m-inert-glyph">${esc(it.glyph)}</span>` : '';
      return `<button type="button" class="m-wd-item${it.big ? ' m-wd-big' : ''}${it.tile ? ` m-wd-tile m-wd-tile-${esc(it.action.replace(/[^a-z-]/g, '-'))}` : ''}" `
        + `data-ws-action="${esc(it.action)}">${glyph}${esc(it.label)}</button>`;
    }).join('')
    + `</div><div class="m-wd-group-label">${esc(g.label)}</div></div>`).join('');
  // The tab row: the active tab is the one real tab; the app's other tabs
  // are inert imagery (0056), drawn in the app's order around it.
  const tabs = r === undefined ? ''
    : (r.tabs && r.tabs.length ? r.tabs : [r.tab]).map((name) => name === r.tab
      ? `<button type="button" class="m-wd-tab m-wd-tab-on" role="tab" aria-selected="true">${esc(name)}</button>`
      : inertItem({ label: name }, 'm-wd-tab')).join('');
  const right = r?.right?.length ? `<div class="m-wd-tabs-end" aria-hidden="true">${r.right.map((i) => inertItem(i, '')).join('')}</div>` : '';
  return `<div class="m-capability m-wd-ribbon">`
    + `<div class="m-wd-tabs" role="tablist" aria-label="Ribbon tabs">${tabs}${right}</div>`
    + `<div class="m-wd-band">${groups}</div></div>`;
};
renderers.set('doc-ribbon@0.1.0', docRibbon);

/** The view switcher: one pill per view the host can show, the showing one
 *  lit; the New button on the right, drawn only when the host will create. */
const viewSwitcher: CapabilityRenderer = (_node, input) => {
  const v = input?.capability === 'view-switcher' ? input.views : undefined;
  const pills = (v?.views ?? []).map((w) =>
    `<button type="button" class="m-viewswitch-item${w.active ? ' m-viewswitch-on' : ''}" role="tab" aria-selected="${w.active}"`
    + ` data-morph-view="${esc(w.id)}">${ntGlyph(NT_TABLE, '', 16)}<span>${esc(w.label)}</span></button>`).join('');
  const tools = v?.inert?.length ? `<div class="m-tools-inert" aria-hidden="true">${v.inert.map((i) => inertItem(i, 'm-tool-inert')).join('')}</div>` : '';
  const strip = v?.strip?.length ? `<div class="m-inert-chips" aria-hidden="true">${v.strip.map((i) => inertItem(i, 'm-inert-chip')).join('')}</div>` : '';
  return `<div class="m-capability m-viewswitch"><div class="m-viewswitch-list" role="tablist" aria-label="Views">${pills}</div>`
    + tools
    + (v?.canCreate ? `<button type="button" class="m-viewswitch-new" data-ws-action="new">New`
      + (v.createMark ? `<span class="m-viewswitch-new-mark" aria-hidden="true">${esc(v.createMark)}</span>` : '')
      + `</button>` : '')
    + strip
    + `</div>`;
};
renderers.set('view-switcher@0.1.0', viewSwitcher);

/**
 * Which slots a registered renderer actually CONSUMES.
 *
 * `CAPABILITY_SLOTS` in the vocabulary says which slots a capability *has* —
 * `document-flow` declares `body`, `header`, `footer`, `footnotes`. That is a
 * claim about the module's design. This says which of them the code in this
 * file puts on screen, which is a different claim and, until now, an unmade
 * one.
 *
 * The gap between the two was silent and total. A spec may legally fill a
 * declared slot; the validator checks the name against the closed set and
 * passes it; the compiler emits the children into the IR. Then the renderer's
 * `capability` case renders `renderCapability(n, input)` and never looks at
 * `n.children` — so the content was accepted four times and drawn zero times,
 * with no diagnostic anywhere. Measured on a two-column `document-flow` node
 * carrying a title in `header` and an author in `footer`: the spec validated
 * with no problems, both fields compiled into the IR, and neither string
 * appeared in the output.
 *
 * Both registered renderers draw their own legends from `input`, so both
 * consume nothing — an empty list is the honest entry, not a missing one.
 */
const CONSUMED_SLOTS = new Map<string, readonly string[]>([
  ['document-flow@0.1.0', ['body', 'header', 'footer', 'footnotes']],
  ['continuous-plot@0.1.0', []],
  ['force-graph@0.2.0', []],
  // The two entries this table was missing while `renderers` held five. They
  // changed nothing observable — a missing entry and an empty list produce the
  // same result — which is exactly why they drifted, and why decision 0019
  // makes one call write both maps.
  ['calendar@0.1.0', []],
  ['timeline@0.1.0', []],
  ['workspace-ribbon@0.1.0', []],
  ['workspace-window@0.1.0', []],
  ['workspace-tabs@0.1.0', []],
  ['workspace-status@0.1.0', []],
  ['workspace-search@0.1.0', []],
  ['workspace-nav@0.1.0', []],
  ['workspace-topbar@0.1.0', []],
  ['workspace-pagehead@0.1.0', []],
  ['view-switcher@0.1.0', []],
  ['doc-actions@0.1.0', []],
  ['doc-ribbon@0.1.0', []],
]);

/**
 * Register one complete capability, with the spec validator AND the renderer.
 *
 * **Registration runs trusted host code.** Calling this is equivalent to
 * running the implementation with the page's authority: the renderer can read
 * ambient state and touch the DOM directly, so nothing about the value it
 * returns can make an untrusted module safe. @Codex ruled that explicitly on
 * decision 0019 — *"constraining or sanitising only its returned value cannot
 * turn that code into an untrusted plugin boundary ... Claiming otherwise would
 * provide a security property the API does not have."*
 *
 * **A renderer returning HTML escapes every user-derived value.** That is the
 * other half of the contract and it is not optional. `esc()` above is the
 * minimum example: `&`, `<`, `>`, `"` and `'`. The layout spec remains data;
 * this API is the one place host code enters, and it enters by invitation.
 *
 * Both halves are written in one call because two doors to one concept is what
 * already drifted here — `renderers` held five entries while `CONSUMED_SLOTS`
 * held three. The implementation is checked BEFORE the descriptor registry is
 * touched, and the writes after it cannot fail, so a caller can never leave a
 * descriptor registered with no renderer behind it.
 */
export function registerCapability(
  descriptor: ModuleDescriptor, implementation: CapabilityImplementation,
): void {
  if (implementation === null || typeof implementation !== 'object') {
    throw new Error('Invalid capability implementation: expected an object');
  }
  if (typeof implementation.render !== 'function') {
    throw new Error('Invalid capability implementation: render must be a function');
  }
  if (!Array.isArray(implementation.consumesSlots)
      || implementation.consumesSlots.some((slot) => typeof slot !== 'string')
      || new Set(implementation.consumesSlots).size !== implementation.consumesSlots.length
      || implementation.consumesSlots.some((slot) => !descriptor.slots.includes(slot))) {
    throw new Error(
      'Invalid capability implementation: consumesSlots must name unique declared slots');
  }

  const consumed = Object.freeze([...implementation.consumesSlots]);
  const registered = registerModule(descriptor);
  const key = `${registered.id}@${registered.version}`;
  renderers.set(key, implementation.render);
  CONSUMED_SLOTS.set(key, consumed);
}

/**
 * Slots this node fills that nothing will draw.
 *
 * Returns the names, so a refusal can say WHICH — "slot content is ignored" is
 * the kind of message that sends someone reading the wrong file.
 */
export function unconsumedSlots(node: IRNode, childSlots: readonly (string | undefined)[]): readonly string[] {
  const filled = [...new Set(childSlots.filter((s): s is string => s !== undefined))];
  if (filled.length === 0) return [];
  const key = node.capability && node.capabilityVersion
    ? `${node.capability}@${node.capabilityVersion}`
    : undefined;
  // No renderer at all: nothing consumes anything, so everything filled is
  // unconsumed. That is the `not yet implemented` case, where slot content
  // was previously dropped in silence ON TOP of an already-visible refusal.
  const consumed = key ? CONSUMED_SLOTS.get(key) : undefined;
  if (!consumed) return filled;
  return filled.filter((s) => !consumed.includes(s));
}

/** The wrapper a composed part is drawn inside, or nothing.
 *
 *  AN ANCESTOR, NOT THE ELEMENT: `.m-app-fibery .m-nav` is a descendant
 *  selector, so a part wearing the class on ITSELF is matched by none of that
 *  look's rules. Measured that way first — the class was in the DOM and every
 *  painted value was still the base's.
 *
 *  Layout-transparent (`.m-part{display:contents}` in the sheet, never an
 *  inline style): a box here becomes the shell's grid item in the borrowed
 *  part's place. That moves the part from child to grandchild, so any rule
 *  reaching it with a child combinator needs the wrapped spelling too — which
 *  the generic nav fold carries, for the same reason a composed layout's
 *  `m-capability-anchor` already needed it.
 *
 *  Sanitised to the class grammar rather than trusted: this reaches an
 *  attribute, and a host passing a quote would be writing markup through a
 *  field that says it names classes. Where every class is refused there is NO
 *  wrapper rather than an empty one — a wrapper with no look on it paints
 *  nothing and still changes what a child combinator matches. */
const partWrapper = (input: CapabilityInput | undefined): readonly [string, string] => {
  const worn = (input?.wearing ?? []).filter((c) => /^[a-z][a-z0-9-]*$/.test(c));
  return worn.length ? [`<div class="m-part ${worn.join(' ')}">`, '</div>'] : ['', ''];
};

/** The marker the PRODUCER stamps on a capability's root — task `0238`.
 *
 *  WHY IT IS STAMPED HERE AND NOT BY EACH RENDERER. Every scan of "which
 *  classes can be a capability root" has derived the set with a grep for
 *  `class="m-capability…"` over this file, and every one of them has been
 *  wrong: 13 of the 16 registered renderers stamp that class, and three return
 *  a root without it — `m-plot`, `m-force-graph` and `m-doc-flow`. Five
 *  successive derivations of the root set were built on the 13. A convention
 *  honoured by hand in sixteen functions is a hand-kept list, and this is the
 *  one place that cannot be forgotten: the choke point every capability
 *  already passes through, wrapper and all.
 *
 *  AN ATTRIBUTE, NOT A CLASS, and that is the whole of why this is safe.
 *  `m-capability` is not a marker — `.m-capability{font-size:var(--s0);
 *  color:var(--text-muted)}` — so stamping it on the three would have moved
 *  Word's document sheet from rgb(31,31,31) to rgb(97,97,97) and Obsidian's
 *  graph from rgb(220,221,222) to rgb(154,154,154), measured. An attribute no
 *  rule selects cannot paint, and a clause holds the sheet to that.
 *
 *  The name is the one already in use for exactly this on the refusal path
 *  above, rather than a second vocabulary for one idea. No renderer may write
 *  it by hand — a clause holds that — so a duplicate is impossible by
 *  construction rather than resolved by whichever occurrence comes first.
 *
 *  APPENDED to the opening tag rather than inserted after the tag name, and
 *  the reason is worth stating: nineteen clauses across the render tests pin a
 *  whole opening tag as a substring, and inserting before `class` breaks every
 *  one of them. Those clauses pin more than they need to — this repo already
 *  records what pinning a whole literal costs — but rewriting nineteen of them
 *  is a larger and riskier change than this one is, and it would be churn
 *  inside a commit about something else.
 *
 *  The end of the opening tag is found by SCANNING rather than by a regex,
 *  because a `>` inside an attribute value is legal and a lazy `[^>]*?` would
 *  stop at it. */
const markRoot = (html: string, capability: string): string => {
  const start = html.indexOf('<');
  if (start < 0 || !/[a-zA-Z]/.test(html[start + 1] ?? '')) return html;
  let quote = '';
  for (let i = start + 1; i < html.length; i += 1) {
    const c = html[i]!;
    if (quote) { if (c === quote) quote = ''; continue; }
    if (c === '"' || c === "'") { quote = c; continue; }
    if (c === '>') {
      const selfClosing = html[i - 1] === '/';
      const at = selfClosing ? i - 1 : i;
      return `${html.slice(0, at)} data-capability="${esc(capability)}"${html.slice(at)}`;
    }
  }
  return html;
};

export function renderCapability(
  node: IRNode, input: CapabilityInput | undefined, slots: SlotHTML = {},
): string {
  const key = node.capability && node.capabilityVersion
    ? `${node.capability}@${node.capabilityVersion}`
    : undefined;
  const render = key ? renderers.get(key) : undefined;
  if (!render) {
    const implemented = node.capability
      ? [...renderers.keys()].some((registered) => registered.startsWith(`${node.capability}@`))
      : false;
    const message = implemented
      ? `${esc(node.capability)} module version is unavailable.`
      : `${esc(node.capability)} not yet implemented`;
    return `<div class="m-capability m-capability-refused" data-capability="${esc(node.capability)}" role="note">`
      + `${message}</div>`;
  }
  /* WRAPPED HERE, so every one of the sixteen composes and not just the one
     somebody wired. A refused capability is NOT wrapped — the refusal above
     returns before this, which is right: a note saying the module is missing
     is Morph's own voice, not the borrowed look's. */
  const [open, close] = partWrapper(input);
  const html = render(node, input, slots);
  /* `node.capability` is defined wherever `render` is — `key` cannot be built
     without it — but the compiler cannot see that through the registry lookup,
     so it is read again rather than asserted away. */
  return open + (node.capability ? markRoot(html, node.capability) : html) + close;
}
